import { Blockbench } from './api';
import { Dialog } from './interface/dialog';
import { setProjectTitle } from './interface/interface';
import { Filesystem } from './file_system';
import { tl } from './languages';
import { JSZip } from './lib/libs';

/**
 * Product features that belong to 3dBlock World rather than to the upstream
 * Blockbench implementation. Keeping these in one module makes upstream
 * updates safer and gives the application a stable extension point.
 */

const APP_NAME = '3dBlock World';
const META_KEY = '3dbw';
const ASSET_STORAGE_KEY = '3dbw_assets';
const HISTORY_STORAGE_PREFIX = '3dbw_history_';
const MAX_HISTORY_ITEMS = 12;

function text(key: string, fallback: string): string {
	return tl(key, undefined, fallback);
}

export type TargetProfile = {
	id: string
	name: string
	description: string
	format: string
	maxCubes: number
	maxTriangles: number
	maxTextureSize: number
	maxTextures: number
	identifierRequired?: boolean
	requireBones?: boolean
}

export type AuditIssue = {
	severity: 'error' | 'warning' | 'info'
	code: string
	message: string
	hint?: string
}

export type AuditReport = {
	profile: TargetProfile
	issues: AuditIssue[]
	score: number
	stats: ProjectStats
}

export type ProjectStats = {
	cubes: number
	meshes: number
	groups: number
	textures: number
	animations: number
	triangles: number
	maxTextureSize: number
	texturePixels: number
	missingFaceTextures: number
	uvOutOfBounds: number
	unusedTextures: number
	externalTextures: number
	duplicateNames: string[]
}

export const TargetProfiles: Record<string, TargetProfile> = {
	bedrock_entity: {
		id: 'bedrock_entity',
		name: 'Minecraft Bedrock Varlığı (Entity)',
		description: 'Bedrock varlık geometrisi, animasyonları, dokuları ve kontrolcüleri',
		format: 'bedrock',
		maxCubes: 500,
		maxTriangles: 6000,
		maxTextureSize: 2048,
		maxTextures: 16,
		identifierRequired: true,
		requireBones: true,
	},
	bedrock_block: {
		id: 'bedrock_block',
		name: 'Minecraft Bedrock Bloğu',
		description: 'Bedrock özel blok geometrisi ve kaynak paketi çıktısı',
		format: 'bedrock_block',
		maxCubes: 256,
		maxTriangles: 3072,
		maxTextureSize: 1024,
		maxTextures: 1,
		identifierRequired: true,
	},
	java_item: {
		id: 'java_item',
		name: 'Minecraft Java Öğesi / Bloğu',
		description: 'Atlas dostu dokulara sahip Java model JSON',
		format: 'java_block',
		maxCubes: 100,
		maxTriangles: 1200,
		maxTextureSize: 1024,
		maxTextures: 8,
	},
	game_asset: {
		id: 'game_asset',
		name: 'Oyun Varlığı / GLB',
		description: 'Blender, Unity, Godot veya Unreal için taşınabilir low-poly varlık',
		format: 'free',
		maxCubes: 2000,
		maxTriangles: 50000,
		maxTextureSize: 4096,
		maxTextures: 32,
	},
	mobile_asset: {
		id: 'mobile_asset',
		name: 'Mobil / Web Optimize',
		description: 'Mobil ve tarayıcı deneyimleri için optimize edilmiş bütçe',
		format: 'free',
		maxCubes: 300,
		maxTriangles: 12000,
		maxTextureSize: 1024,
		maxTextures: 8,
	},
};

const ProjectTemplates = {
	bedrock_entity: {
		name: 'Bedrock Varlığı (Entity)',
		format: 'bedrock',
		profile: 'bedrock_entity',
		resolution: 64,
		identifier: 'varligim',
	},
	bedrock_block: {
		name: 'Bedrock Bloğu',
		format: 'bedrock_block',
		profile: 'bedrock_block',
		resolution: 16,
		identifier: 'blogum',
	},
	java_item: {
		name: 'Java Öğesi / Bloğu',
		format: 'java_block',
		profile: 'java_item',
		resolution: 32,
		identifier: 'ogem',
	},
	game_asset: {
		name: 'Genel Oyun Varlığı',
		format: 'free',
		profile: 'game_asset',
		resolution: 64,
		identifier: 'varligim',
	},
	texture: {
		name: 'Doku / Piksel Sanatı',
		format: 'image',
		profile: 'game_asset',
		resolution: 64,
		identifier: 'dokum',
	},
};

function clone<T>(value: T): T {
	return JSON.parse(JSON.stringify(value));
}

function makeUuid(): string {
	return typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : guid();
}

function slugify(value: string, fallback = 'asset'): string {
	let result = (value || '').toLowerCase().trim()
		.replace(/[^a-z0-9_\-.]+/g, '_')
		.replace(/^[_\-.]+|[_\-.]+$/g, '')
		.slice(0, 48);
	return result || fallback;
}

function getMetadata(create = true): Record<string, any> | null {
	if (!Project || typeof Project !== 'object') return null;
	if (!Project[META_KEY] && create) {
		Project[META_KEY] = {
			version: 1,
			target_profile: 'game_asset',
			namespace: 'my_pack',
			tags: [],
			created_at: new Date().toISOString(),
			updated_at: new Date().toISOString(),
		};
	}
	return Project[META_KEY] || null;
}

function touchProject(): void {
	const metadata = getMetadata();
	if (metadata) metadata.updated_at = new Date().toISOString();
	if (Project) Project.saved = false;
}

function getProfile(id?: string): TargetProfile {
	const metadata = getMetadata(false);
	return TargetProfiles[id || metadata?.target_profile || 'game_asset'] || TargetProfiles.game_asset;
}

function getArray(value: any): any[] {
	return Array.isArray(value) ? value : [];
}

function getProjectStats(): ProjectStats {
	const cubes = typeof Cube !== 'undefined' ? getArray(Cube.all) : [];
	const meshes = typeof Mesh !== 'undefined' ? getArray(Mesh.all) : [];
	const groups = typeof Group !== 'undefined' ? getArray(Group.all) : [];
	const textures = typeof Texture !== 'undefined' ? getArray(Texture.all) : [];
	const animations = Project ? getArray(Project.animations) : [];
	let triangles = cubes.length * 12;
	for (const mesh of meshes) {
		for (const face of Object.values(mesh.faces || {})) {
			const vertexCount = getArray((face as any).vertices).length;
			if (vertexCount >= 3) triangles += vertexCount - 2;
		}
	}

	let missingFaceTextures = 0;
	let uvOutOfBounds = 0;
	const usedTextureIds = new Set<string>();
	for (const cube of cubes) {
		for (const face of Object.values(cube.faces || {}) as any[]) {
			if (face.enabled !== false && face.texture === null) missingFaceTextures++;
			if (face.texture) usedTextureIds.add(String(face.texture));
			const uv = face.uv;
			if (Array.isArray(uv) && uv.length >= 4 && Project) {
				const width = Number(Project.texture_width || 0);
				const height = Number(Project.texture_height || 0);
				if (uv[0] < 0 || uv[1] < 0 || uv[2] > width || uv[3] > height) uvOutOfBounds++;
			}
		}
	}
	for (const mesh of meshes) {
		for (const face of Object.values(mesh.faces || {}) as any[]) {
			if (face.texture) usedTextureIds.add(String(face.texture));
		}
	}

	const names = new Map<string, number>();
	for (const element of [...cubes, ...meshes, ...groups]) {
		const name = String(element.name || '').trim();
		if (name) names.set(name, (names.get(name) || 0) + 1);
	}
	const duplicateNames = [...names.entries()]
		.filter(([, count]) => count > 1)
		.map(([name]) => name);
	const maxTextureSize = textures.reduce((max, texture) => Math.max(max, texture.width || 0, texture.height || 0), 0);
	const texturePixels = textures.reduce((total, texture) => total + ((texture.width || 0) * (texture.display_height || texture.height || 0)), 0);

	return {
		cubes: cubes.length,
		meshes: meshes.length,
		groups: groups.length,
		textures: textures.length,
		animations: animations.length,
		triangles,
		maxTextureSize,
		texturePixels,
		missingFaceTextures,
		uvOutOfBounds,
		unusedTextures: textures.filter(texture => texture.uuid && !usedTextureIds.has(String(texture.uuid))).length,
		externalTextures: textures.filter(texture => !!texture.path && !texture.source).length,
		duplicateNames,
	};
}

export function auditProject(profileId?: string): AuditReport {
	const profile = getProfile(profileId);
	const stats = getProjectStats();
	const issues: AuditIssue[] = [];
	const metadata = getMetadata(false);

	const add = (severity: AuditIssue['severity'], code: string, message: string, hint?: string) => {
		issues.push({severity, code, message, hint});
	};

	if (!Project) {
		add('info', 'NO_PROJECT', 'Open or create a project to run the production audit.');
		return {profile, issues, score: 0, stats};
	}
	if (!Project.name && !Project.geometry_name) add('warning', 'PROJECT_NAME', 'The project has no descriptive name.', 'Use the Project Studio to add a name and identifier.');
	if (stats.cubes > profile.maxCubes) add('error', 'CUBE_BUDGET', `${stats.cubes} cubes exceed the ${profile.maxCubes} cube budget for ${profile.name}.`, 'Split the asset into variants or reduce detail.');
	if (stats.triangles > profile.maxTriangles) add('error', 'TRIANGLE_BUDGET', `${stats.triangles.toLocaleString()} estimated triangles exceed the ${profile.maxTriangles.toLocaleString()} triangle budget.`, 'Use fewer cubes, simplify meshes, or create a high/low detail pair.');
	if (stats.maxTextureSize > profile.maxTextureSize) add('error', 'TEXTURE_SIZE', `A texture is ${stats.maxTextureSize}px; ${profile.name} allows up to ${profile.maxTextureSize}px.`, 'Resize the texture in the texture editor or choose a higher target profile.');
	if (stats.textures > profile.maxTextures) add('warning', 'TEXTURE_COUNT', `${stats.textures} textures exceed the recommended limit of ${profile.maxTextures}.`, 'Pack related textures into an atlas.');
	if (stats.missingFaceTextures > 0) add('warning', 'UNTEXTURED_FACES', `${stats.missingFaceTextures} visible cube faces have no texture assigned.`, 'Assign a texture or disable export for intentional invisible faces.');
	if (stats.uvOutOfBounds > 0) add('warning', 'UV_OUT_OF_BOUNDS', `${stats.uvOutOfBounds} face UV mapping(s) extend outside the project texture resolution.`, 'Move the UVs back into the atlas or intentionally enable texture wrapping for this target.');
	if (stats.unusedTextures > 0) add('info', 'UNUSED_TEXTURES', `${stats.unusedTextures} texture(s) are not referenced by a model face.`, 'Remove unused maps or keep them only when they are part of a planned runtime material.');
	if (stats.externalTextures > 0) add('warning', 'EXTERNAL_TEXTURES', `${stats.externalTextures} texture(s) depend on an external file path.`, 'Embed or package the bitmap before sending the asset to another machine.');
	if (stats.duplicateNames.length) add('warning', 'DUPLICATE_NAMES', `Duplicate names found: ${stats.duplicateNames.slice(0, 5).join(', ')}${stats.duplicateNames.length > 5 ? '…' : ''}.`, 'Use unique, predictable names for reliable exports and animations.');
	if (profile.format && Format?.id && profile.format !== Format.id) add('warning', 'FORMAT_TARGET', `The current format (${Format.id}) does not match the selected target profile (${profile.format}).`, 'Choose the matching project format or update the validation target in Project Metadata.');
	const rawIdentifier = String(Project.geometry_name || metadata?.identifier || '').trim();
	if (profile.identifierRequired && (!rawIdentifier || slugify(rawIdentifier, '') !== rawIdentifier)) add('error', 'IDENTIFIER', 'A valid resource identifier is required for this target.', 'Set a lowercase identifier using letters, numbers, dots, hyphens, or underscores.');
	if (profile.requireBones && stats.groups === 0 && stats.cubes > 0) add('warning', 'NO_BONES', 'The entity has no bone groups.', 'Create at least one bone group before exporting a Bedrock entity.');
	if (stats.textures === 0 && stats.cubes > 0) add('warning', 'NO_TEXTURES', 'The model contains geometry but no textures.', 'Add a texture or switch to a format that intentionally uses materials.');

	const errors = issues.filter(issue => issue.severity === 'error').length;
	const warnings = issues.filter(issue => issue.severity === 'warning').length;
	const score = Math.max(0, Math.min(100, 100 - errors * 25 - warnings * 8));
	return {profile, issues, score, stats};
}

function getTemplateOptions(): Record<string, string> {
	return Object.fromEntries(Object.entries(ProjectTemplates).map(([id, template]) => [id, template.name]));
}

const WorkflowSteps = [
	{title: '1. Bir hedefle başlayın', body: 'Bedrock Varlığı, Bedrock Bloğu, Java Öğesi veya taşınabilir bir GLB profili seçin. Hedef bütçeleri ve dışa aktarım yapısını belirler.'},
	{title: '2. Düzenli bir yapı oluşturun', body: 'Öngörülebilir kemik ve eleman adları kullanın, pivot noktalarını belirleyin ve animasyona başlamadan önce dokuları atayın.'},
	{title: '3. Dışa aktarmadan önce denetleyin', body: 'Üretim Denetimi; küpleri, tahmini üçgenleri, doku sınırlarını, tanımlayıcıları, kemikleri ve eksik yüzey dokularını kontrol eder.'},
	{title: '4. Teslimat için paketleyin', body: 'Dışa Aktarma Stüdyosu düzenlenebilir kaynak, model çıktısı, dokular, bildirimler ve BENIOKU (README) dosyasını taşınabilir tek bir ZIPte toplar.'},
	{title: '5. İnceleyin ve işbirliği yapın', body: 'Anlık görüntüler, çevrimdışı paylaşım dosyaları, canlı oturumlar ve Sunum Stüdyosu ile net bir teslimat hazırlayın.'},
];

function openWorkflowGuide(): void {
	const dialog = new Dialog({
		id: 'dbw_workflow_guide',
		title: '3dBlock World Production Guide',
		width: 620,
		singleButton: true,
		component: {
			data() { return {step: 0, steps: WorkflowSteps}; },
			methods: {
				next() { this.step = Math.min(this.steps.length - 1, this.step + 1); },
				previous() { this.step = Math.max(0, this.step - 1); },
				studio() { dialog.hide(); openProjectStudio(); },
			},
			template: `
				<div class="dbw_guide_panel">
					<div class="dbw_guide_progress"><span v-for="(item, index) in steps" :key="index" :class="{active: index === step, complete: index < step}"></span></div>
					<div class="dbw_guide_step"><small>WORKFLOW {{ step + 1 }} / {{ steps.length }}</small><h2>{{ steps[step].title }}</h2><p>{{ steps[step].body }}</p></div>
					<div class="dbw_guide_actions"><button type="button" @click="previous" :disabled="step === 0">Previous</button><button type="button" @click="next" v-if="step < steps.length - 1">Next</button><button type="button" @click="studio" v-else>Open Project Studio</button></div>
				</div>
			`,
		},
	});
	dialog.show();
}

function openPluginMarketplace(): void {
	if ((BarItems as any).plugins_window) {
		(BarItems as any).plugins_window.click();
	} else {
		Blockbench.showQuickMessage('Plugin marketplace is not available until the interface finishes loading');
	}
}

function openProjectStudio(): void {
	let previousTemplate = 'bedrock_entity';
	const dialog = new Dialog({
		id: 'dbw_project_studio',
		title: text('dbw.project_studio.title', '3dBlock World Project Studio'),
		width: 560,
		form: {
			template: {label: text('dbw.project_studio.template', 'Project template'), type: 'select', options: getTemplateOptions(), value: 'bedrock_entity'},
			name: {label: text('dbw.project_studio.name', 'Project name'), type: 'text', value: 'My 3D Asset'},
			identifier: {label: text('dbw.project_studio.identifier', 'Resource identifier'), type: 'text', value: 'my_entity'},
			namespace: {label: text('dbw.project_studio.namespace', 'Namespace'), type: 'text', value: 'my_pack'},
			target_profile: {label: text('dbw.project_studio.target', 'Validation target'), type: 'select', options: Object.fromEntries(Object.entries(TargetProfiles).map(([id, profile]) => [id, profile.name])), value: 'bedrock_entity'},
			texture_width: {label: text('dbw.project_studio.width', 'Texture width'), type: 'number', value: 64, min: 1, max: 4096, step: 1},
			texture_height: {label: text('dbw.project_studio.height', 'Texture height'), type: 'number', value: 64, min: 1, max: 4096, step: 1},
			tags: {label: text('dbw.project_studio.tags', 'Tags'), type: 'text', value: 'character, minecraft'},
			description: {label: text('dbw.project_studio.description', 'Description'), type: 'textarea', height: 80, value: ''},
		},
		onFormChange(result) {
			const template = ProjectTemplates[result.template as keyof typeof ProjectTemplates];
			if (!template) return;
			if (previousTemplate === result.template) return;
			previousTemplate = result.template as string;
			this.setFormValues({target_profile: template.profile, identifier: template.identifier, texture_width: template.resolution, texture_height: template.resolution}, false);
		},
		onConfirm(result) {
			const template = ProjectTemplates[result.template as keyof typeof ProjectTemplates] || ProjectTemplates.game_asset;
			const format = Formats[template.format] || Formats.free;
			if (!format) {
				Blockbench.showQuickMessage('Unable to create the selected project format');
				return false;
			}
			format.new();
			if (!Project) return false;
			Project.name = String(result.name || template.name).trim() || template.name;
			Project.geometry_name = slugify(String(result.identifier || template.identifier), template.identifier);
			Project.texture_width = Number(result.texture_width) || template.resolution;
			Project.texture_height = Number(result.texture_height) || template.resolution;
			Project[META_KEY] = {
				version: 1,
				target_profile: result.target_profile || template.profile,
				identifier: Project.geometry_name,
				namespace: slugify(String(result.namespace || 'my_pack'), 'my_pack'),
				description: String(result.description || '').trim(),
				tags: String(result.tags || '').split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 12),
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
			};
			Project.saved = false;
			setTimeout(() => openAuditDialog(), 150);
		},
	});
	dialog.show();
}

let auditDialog: Dialog | null = null;
function auditComponent(): any {
	return {
		data() {
			const profile = getProfile();
			return {profileId: profile.id, report: auditProject(profile.id), profiles: Object.values(TargetProfiles)};
		},
		methods: {
			refresh() {
				this.report = auditProject(this.profileId);
				const metadata = getMetadata();
				if (metadata) metadata.target_profile = this.profileId;
			},
			fixIdentifier() {
				if (!Project) return;
				Project.geometry_name = slugify(Project.geometry_name || Project.name, 'model');
				touchProject();
				this.refresh();
			},
			createSnapshot() {
				saveHistorySnapshot('Before production fixes');
				Blockbench.showQuickMessage('Production snapshot created');
			},
			openStudio() {
				auditDialog?.hide();
				openProjectStudio();
			},
		},
		template: `
			<div class="dbw_audit_panel">
				<div class="dbw_audit_header">
					<div><strong>Üretim Denetimi</strong><small>{{ report.profile.description }}</small></div>
					<select v-model="profileId" @change="refresh"><option v-for="profile in profiles" :value="profile.id">{{ profile.name }}</option></select>
				</div>
				<div class="dbw_metrics">
					<div><strong>{{ report.score }}</strong><span>${text('dbw.audit.score', 'Kalite puanı')}</span></div>
					<div><strong>{{ report.stats.cubes }}</strong><span>${text('dbw.audit.cubes', 'Küp')}</span></div>
					<div><strong>{{ report.stats.triangles.toLocaleString() }}</strong><span>${text('dbw.audit.triangles', 'Üçgen')}</span></div>
					<div><strong>{{ report.stats.textures }}</strong><span>${text('dbw.audit.textures', 'Doku')}</span></div>
					<div><strong>{{ report.stats.uvOutOfBounds }}</strong><span>UV Sorunları</span></div>
					<div><strong>{{ report.stats.unusedTextures }}</strong><span>Kullanılmayan Dokular</span></div>
				</div>
				<div class="dbw_audit_issue" v-for="issue in report.issues" :key="issue.code + issue.message" :class="issue.severity">
					<i class="material-icons">{{ issue.severity === 'error' ? 'error' : issue.severity === 'warning' ? 'warning' : 'info' }}</i>
					<div><strong>{{ issue.code }}</strong><p>{{ issue.message }}</p><small v-if="issue.hint">{{ issue.hint }}</small></div>
				</div>
				<div class="dbw_empty_state" v-if="!report.issues.length">${text('dbw.audit.ready', 'Dışa aktarmaya hazır. Bu hedef için sorun bulunamadı.')}</div>
				<div class="dbw_audit_actions">
					<button type="button" @click="refresh">${text('dbw.audit.rerun', 'Denetimi Yeniden Çalıştır')}</button>
					<button type="button" @click="fixIdentifier" v-if="report.issues.some(issue => issue.code === 'IDENTIFIER')">${text('dbw.audit.fix_identifier', 'Tanımlayıcıyı Düzelt')}</button>
					<button type="button" @click="createSnapshot">${text('dbw.audit.snapshot', 'Anlık Görüntü Oluştur')}</button>
					<button type="button" @click="openStudio">Proje Stüdyosu</button>
				</div>
			</div>
		`,
	};
}

function openAuditDialog(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Create or open a project first');
		return;
	}
	auditDialog = auditDialog || new Dialog({
		id: 'dbw_audit',
		title: text('dbw.audit.title', '3dBlock World Production Audit'),
		width: 720,
		singleButton: true,
		component: auditComponent(),
	});
	auditDialog.show();
	if (auditDialog.content_vue) auditDialog.content_vue.$data.report = auditProject(auditDialog.content_vue.$data.profileId);
}

function dataUrlToZipEntry(source: string): {data: string, base64: boolean} | null {
	if (!source || !source.startsWith('data:')) return null;
	const comma = source.indexOf(',');
	if (comma < 0) return null;
	const header = source.slice(0, comma);
	return {data: source.slice(comma + 1), base64: header.includes(';base64')};
}

async function exportAssetPack(): Promise<void> {
	if (!Project) return;
	const base = slugify(Project.name || Project.geometry_name || 'asset', 'asset');
	const metadata = getMetadata(false) || {};
	const archive = new JSZip();
	const profile = getProfile(metadata.target_profile);
	const report = auditProject(profile.id);
	const isBedrock = Format?.id === 'bedrock' || Format?.id === 'bedrock_block';
	const isBlock = Format?.id === 'bedrock_block';
	const resourceRoot = isBedrock ? 'resource_pack' : '';
	const behaviorRoot = isBedrock ? 'behavior_pack' : '';
	const resourceUuid = isBedrock ? makeUuid() : '';
	const behaviorUuid = isBedrock ? makeUuid() : '';
	const resourceIdentifier = slugify(Project.geometry_name || metadata.identifier || base, base);
	const namespace = slugify(metadata.namespace || '3dbw', '3dbw');
	const identifier = `${namespace}:${resourceIdentifier}`;
	const textures = getArray(Texture.all);
	const defaultTextureName = textures[0] ? slugify(textures[0].name || base, base) : base;
	const readme = [
		`# ${Project.name || base}`,
		'',
		`Generated by ${APP_NAME}.`,
		`Target: ${profile.name}`,
		`Quality score: ${report.score}/100`,
		'',
		metadata.description || 'Production asset package.',
		'',
		'## Contents',
		'- project.bbmodel: editable source project',
		`- model output: ${isBedrock ? 'resource_pack/' : ''}target-specific model or portable GLB`,
		`- ${isBedrock ? 'resource_pack/' : Format?.id === 'java_block' ? `assets/${namespace}/` : ''}textures/: embedded project textures`,
		...(isBedrock ? ['- behavior_pack/: starter entity or block definition'] : []),
	].join('\n');
	archive.file('README.md', readme);
	archive.file('metadata.json', JSON.stringify({app: APP_NAME, generated_at: new Date().toISOString(), profile, report}, null, 2));

	const sourceProject = Codecs.project.compile({raw: false, minify: true});
	archive.file(`${base}.bbmodel`, sourceProject);
	let outputName = `${base}.glb`;
	let outputContent: any;
	if (isBedrock) {
		const modelFolder = isBlock ? 'models/blocks' : 'models/entity';
		outputName = `${resourceRoot}/${modelFolder}/${base}.geo.json`;
		outputContent = Codecs.bedrock.compile({});
		const manifest = {
			format_version: 2,
			header: {name: Project.name || base, description: 'Generated by 3dBlock World', uuid: resourceUuid, version: [1, 0, 0]},
			modules: [{type: 'resources', uuid: makeUuid(), version: [1, 0, 0]}],
		};
		archive.file(`${resourceRoot}/manifest.json`, JSON.stringify(manifest, null, 2));
		const behaviorManifest = {
			format_version: 2,
			header: {name: `${Project.name || base} Behavior`, description: 'Generated by 3dBlock World', uuid: behaviorUuid, version: [1, 0, 0]},
			modules: [{type: 'data', uuid: makeUuid(), version: [1, 0, 0]}],
			dependencies: [{uuid: resourceUuid, version: [1, 0, 0]}],
		};
		archive.file(`${behaviorRoot}/manifest.json`, JSON.stringify(behaviorManifest, null, 2));
		if (isBlock) {
			archive.file(`${behaviorRoot}/blocks/${base}.json`, JSON.stringify({
				format_version: '1.20.0',
				'minecraft:block': {
					description: {identifier, menu_category: {category: 'construction'}},
					components: {
						'minecraft:geometry': {identifier: `geometry.${resourceIdentifier}`},
						'minecraft:material_instances': {'*': {texture: defaultTextureName, render_method: 'opaque'}},
					},
				},
			}, null, 2));
		} else {
			archive.file(`${behaviorRoot}/entities/${base}.json`, JSON.stringify({
				format_version: '1.20.0',
				'minecraft:entity': {
					description: {identifier, is_spawnable: true, is_summonable: true},
					components: {'minecraft:health': {value: 20, max: 20}},
				},
			}, null, 2));
		}
		if (!isBlock) {
			const clientDescription: any = {
				identifier,
				materials: {default: 'entity_alphatest'},
				textures: {default: `textures/entity/${defaultTextureName}`},
				geometry: {default: `geometry.${resourceIdentifier}`},
			};
			archive.file(`${resourceRoot}/entity/${base}.entity.json`, JSON.stringify({format_version: '1.10.0', 'minecraft:client_entity': {description: clientDescription}}, null, 2));
		}
	} else if (Format?.id === 'java_block') {
		outputName = `assets/${namespace}/models/${base}.json`;
		outputContent = Codecs.java_block.compile({});
		archive.file('pack.mcmeta', JSON.stringify({pack: {pack_format: 15, description: Project.name || base}}, null, 2));
	} else {
		outputContent = await Codecs.gltf.compile({encoding: 'binary', embed_textures: true, animations: true});
	}
	archive.file(outputName, typeof outputContent === 'string' ? outputContent : outputContent);

	for (const texture of textures) {
		const entry = dataUrlToZipEntry(texture.source);
		if (!entry) continue;
		const name = slugify(texture.name || `texture_${texture.id}`, `texture_${texture.id}`);
		const textureFolder = isBedrock
			? `${resourceRoot}/textures/${isBlock ? 'blocks' : 'entity'}`
			: Format?.id === 'java_block' ? `assets/${namespace}/textures` : 'textures';
		archive.file(`${textureFolder}/${name}.png`, entry.data, {base64: entry.base64});
	}
	if (isBedrock && isBlock) {
		archive.file(`${resourceRoot}/textures/terrain_texture.json`, JSON.stringify({resource_pack_name: base, texture_name: 'atlas.terrain', texture_data: {[defaultTextureName]: {textures: `textures/blocks/${defaultTextureName}`}}}, null, 2));
	}

	const content = await archive.generateAsync({type: 'blob'});
	Filesystem.exportFile({
		resource_id: 'model',
		type: '3dBlock World Asset Pack',
		extensions: ['zip'],
		name: `${base}_pack`,
		content,
		savetype: 'zip',
	});
}

type AssetRecord = {id: string, name: string, path: string, type: string, tags: string[], created_at: string};
function loadAssets(): AssetRecord[] {
	try {
		const value = JSON.parse(localStorage.getItem(ASSET_STORAGE_KEY) || '[]');
		return Array.isArray(value) ? value : [];
	} catch (error) {
		return [];
	}
}
function saveAssets(assets: AssetRecord[]): void {
	localStorage.setItem(ASSET_STORAGE_KEY, JSON.stringify(assets.slice(0, 200)));
}
function importAssets(done?: () => void): void {
	Filesystem.importFile({
		type: '3dBlock World Assets',
		extensions: ['bbmodel', 'json', 'png', 'jpg', 'jpeg', 'gltf', 'glb'],
		multiple: true,
		resource_id: 'model',
		readtype: 'none',
	}, files => {
		const assets = loadAssets();
		for (const file of files || []) {
			const path = file.path || file.name;
			if (assets.some(asset => asset.path === path)) continue;
			const extension = (file.name.split('.').pop() || 'file').toLowerCase();
			assets.unshift({id: makeUuid(), name: file.name.replace(/\.[^.]+$/, ''), path, type: extension, tags: [], created_at: new Date().toISOString()});
		}
		saveAssets(assets);
		done?.();
	});
}
function openAssetFile(asset: AssetRecord): void {
	if (!isApp) {
		Blockbench.showQuickMessage('Açmak için dosyayı web uygulamasında tekrar seçin');
		return;
	}
	const imageTypes = ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'tga'];
	const readtype = imageTypes.includes(asset.type) ? 'image' : asset.type === 'glb' ? 'buffer' : 'text';
	Filesystem.read([asset.path], {readtype}, files => {
		if (!files?.[0]) return;
		if (imageTypes.includes(asset.type)) {
			loadImages(files, undefined);
		} else {
			loadModelFile(files[0], {});
		}
	});
}
function openAssetLibrary(): void {
	const dialog = new Dialog({
		id: 'dbw_asset_library',
		title: text('dbw.library.title', '3dBlock World Varlık Kütüphanesi'),
		width: 720,
		singleButton: true,
		component: {
			data() { return {search: '', assets: loadAssets() as AssetRecord[]}; },
			computed: {
				filtered() {
					const term = this.search.toLowerCase().trim();
					return this.assets.filter(asset => !term || `${asset.name} ${asset.type} ${asset.tags.join(' ')}`.toLowerCase().includes(term));
				},
			},
			methods: {
				refresh() { this.assets = loadAssets(); },
				add() { importAssets(() => this.refresh()); },
				remove(asset: AssetRecord) {
					if (!confirm(`${asset.name} yerel kütüphaneden silinsin mi?`)) return;
					this.assets = loadAssets().filter(item => item.id !== asset.id);
					saveAssets(this.assets);
				},
				open(asset: AssetRecord) { openAssetFile(asset); },
				tag(asset: AssetRecord) {
					Blockbench.textPrompt('Varlık etiketleri', asset.tags.join(', '), value => {
						if (value === undefined) return;
						asset.tags = String(value).split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 12);
						const assets = loadAssets();
						const record = assets.find(item => item.id === asset.id);
						if (record) record.tags = asset.tags;
						saveAssets(assets);
						this.refresh();
					});
				},
			},
			template: `
				<div class="dbw_asset_library">
					<div class="dbw_library_toolbar"><input v-model="search" placeholder="${text('dbw.library.search', 'İsim, tür veya etiket ara...')}"><button type="button" @click="add">${text('dbw.library.add', 'Dosya Ekle')}</button></div>
					<div class="dbw_asset_grid" v-if="filtered.length">
						<div class="dbw_asset_card" v-for="asset in filtered" :key="asset.id">
							<div class="dbw_asset_icon"><i class="material-icons">{{ asset.type === 'png' || asset.type === 'jpg' ? 'image' : asset.type === 'bbmodel' ? 'view_in_ar' : 'insert_drive_file' }}</i></div>
							<strong>{{ asset.name }}</strong><small>{{ asset.type.toUpperCase() }}</small>
							<div class="dbw_asset_tags"><span v-for="tag in asset.tags" :key="tag">#{{ tag }}</span><button type="button" @click="tag(asset)">${text('dbw.library.edit_tags', 'Etiketleri Düzenle')}</button></div>
							<div><button type="button" @click="open(asset)">${text('dbw.library.open', 'Aç')}</button><button type="button" class="dbw_danger" @click="remove(asset)">${text('dbw.library.remove', 'Sil')}</button></div>
						</div>
					</div>
					<div class="dbw_empty_state" v-else>${text('dbw.library.empty', 'Henüz varlık yok. Yerel kütüphanenizi oluşturmak için model, doku veya GLB dosyaları ekleyin.')}</div>
				</div>
			`,
		},
	});
	dialog.show();
}

type ProductionPose = {id: string, name: string, description: string};
const ProductionPoses: ProductionPose[] = [
	{id: 'reset', name: 'Sıfırla / Bağlama Pozu (Bind Pose)', description: 'Tüm kemik gruplarını sıfır rotasyonuna döndürün.'},
	{id: 'idle', name: 'Boşta / Duruş (Idle)', description: 'Dengeli ve ince bir sunum pozu.'},
	{id: 'walk', name: 'Yürüme (Walk)', description: 'Yürüme önizlemesi için dönüşümlü kol ve bacak pozisyonu.'},
	{id: 'attack', name: 'Saldırı (Attack)', description: 'Basit bir eylem önizlemesi için baskın kolu kaldırın.'},
	{id: 'hero', name: 'Kahraman (Hero)', description: 'Küçük resimler ve sunum renderları için açık ve etkileyici duruş.'},
];

function applyProductionPose(poseId: string): void {
	if (!Project) return;
	const groups = getArray(Group?.all);
	if (!groups.length) {
		Blockbench.showQuickMessage('Üretim pozu uygulamadan önce kemik grupları oluşturun');
		return;
	}
	Undo.initEdit({outliner: true, elements: [], groups});
	for (const group of groups) {
		const name = String(group.name || '').toLowerCase();
		const rotation = [0, 0, 0];
		const left = /(^|[_ -])left|left([_ -]|$)|sol/.test(name);
		const right = /(^|[_ -])right|right([_ -]|$)|sag/.test(name);
		const side = left ? -1 : right ? 1 : 0;
		if (poseId === 'idle') {
			if (name.includes('arm') || name.includes('kol')) rotation[2] = side * 4;
			if (name.includes('head') || name.includes('kafa')) rotation[1] = -3;
		} else if (poseId === 'walk') {
			if (name.includes('arm') || name.includes('kol')) rotation[0] = side * 18;
			if (name.includes('leg') || name.includes('foot') || name.includes('bacak') || name.includes('ayak')) rotation[0] = -side * 24;
		} else if (poseId === 'attack') {
			if ((name.includes('arm') || name.includes('kol')) && (right || !left)) rotation[0] = -55;
			if (name.includes('head') || name.includes('kafa')) rotation[1] = -10;
		} else if (poseId === 'hero') {
			if (name.includes('arm') || name.includes('kol')) rotation[2] = side * 16;
			if (name.includes('leg') || name.includes('foot') || name.includes('bacak') || name.includes('ayak')) rotation[0] = side * 5;
		}
		if (group.rotation?.V3_set) group.rotation.V3_set(rotation[0], rotation[1], rotation[2]);
	}
	Canvas.updateAllBones?.();
	Undo.finishEdit('Üretim pozunu uygula');
	touchProject();
}

function openProductionPresets(): void {
	const dialog = new Dialog({
		id: 'dbw_presets',
		title: text('dbw.presets.title', '3dBlock World Üretim Pozları'),
		width: 640,
		singleButton: true,
		component: {
			data() { return {poses: ProductionPoses}; },
			methods: {
				apply(pose: ProductionPose) { applyProductionPose(pose.id); },
				openAnimationPresets() {
					if (typeof Modes !== 'undefined' && Modes.animate && (BarItems as any).apply_animation_preset) {
						(BarItems as any).apply_animation_preset.click();
					} else {
						Blockbench.showQuickMessage('Animasyon önayarlarını kullanmak için Animasyon Moduna geçin ve bir kemik seçin');
					}
				},
			},
			template: `
				<div class="dbw_preset_panel">
					<div class="dbw_preset_intro"><strong>Kullanıma hazır üretim pozları</strong><small>Önizlemeler, ekran görüntüleri ve animasyon taslakları için kemik gruplarına geri alınabilir poz uygulayın.</small></div>
					<div class="dbw_preset_grid"><button v-for="pose in poses" :key="pose.id" type="button" @click="apply(pose)"><i class="material-icons">accessibility_new</i><strong>{{ pose.name }}</strong><small>{{ pose.description }}</small></button></div>
					<div class="dbw_preset_animation"><i class="material-icons">movie_filter</i><div><strong>Animasyon önayarları</strong><small>Animasyon modunda yerleşik bekleme, yürüme ve özel anahtar kare sistemini kullanın.</small></div><button type="button" @click="openAnimationPresets">Animasyon önayarlarını aç</button></div>
				</div>
			`,
		},
	});
	dialog.show();
}

function exportLocalSharePackage(): void {
	if (!Project || !Codecs?.project) return;
	const model = Codecs.project.compile({compressed: false, absolute_paths: false});
	const share = JSON.stringify({
		format: '3dbwshare',
		version: 1,
		app: APP_NAME,
		created_at: new Date().toISOString(),
		project: typeof model === 'string' ? JSON.parse(model) : model,
	}, null, 2);
	Filesystem.exportFile({
		resource_id: 'model',
		type: '3dBlock World Offline Share',
		extensions: ['3dbwshare'],
		name: `${slugify(Project.name || 'asset', 'asset')}_share`,
		content: share,
	});
}

function importLocalSharePackage(done?: () => void): void {
	Filesystem.importFile({
		resource_id: 'model',
		type: '3dBlock World Offline Share',
		extensions: ['3dbwshare'],
		readtype: 'text',
	}, files => {
		try {
			const payload = JSON.parse(String(files?.[0]?.content || ''));
			if (payload.format !== '3dbwshare' || !payload.project) throw new Error('Invalid 3dBlock World share file');
			Codecs.project.load(payload.project, {path: files[0].path || 'shared.bbmodel', no_file: true});
			done?.();
		} catch (error) {
			Blockbench.showMessageBox({title: APP_NAME, message: `Could not import share file: ${error}`, icon: 'error'});
		}
	});
}

function openCollaborationStudio(): void {
	const dialog = new Dialog({
		id: 'dbw_collaboration',
		title: text('dbw.collaboration.title', '3dBlock World İşbirliği'),
		width: 640,
		singleButton: true,
		component: {
			data() { return {active: !!(Project as any)?.EditSession?.active, hasProject: !!Project}; },
			methods: {
				refresh() { this.active = !!(Project as any)?.EditSession?.active; this.hasProject = !!Project; },
				openSession() { (BarItems as any).edit_session?.click(); setTimeout(() => this.refresh(), 300); },
				shareOnline() { (BarItems as any).share_model?.click(); },
				exportOffline() { exportLocalSharePackage(); },
				importOffline() { importLocalSharePackage(() => this.refresh()); },
			},
			template: `
				<div class="dbw_collaboration_panel">
					<div class="dbw_collaboration_status" :class="{active: active}"><i class="material-icons">{{ active ? 'cloud_done' : 'cloud_off' }}</i><div><strong>{{ active ? 'Canlı düzenleme oturumu aktif' : 'Aktif canlı oturum yok' }}</strong><small>{{ active ? 'Yerleşik PeerJS oturumu mevcut projeyi senkronize ediyor.' : 'Bir canlı oturum başlatın veya çevrimdışı paylaşım dosyasını kullanın.' }}</small></div><button type="button" @click="refresh">Yenile</button></div>
					<div class="dbw_collaboration_grid"><button type="button" :disabled="!hasProject" @click="openSession"><i class="material-icons">people</i><strong>Canlı ekip oturumu</strong><small>Sohbet ve eşzamanlı düzenleme içeren güvenli oturum akışını açın.</small></button><button type="button" :disabled="!hasProject" @click="shareOnline"><i class="material-icons">share</i><strong>Çevrimiçi model paylaşımı</strong><small>İnceleme bağlantısı yayınlamak için süreli model paylaşım hizmetini kullanın.</small></button><button type="button" :disabled="!hasProject" @click="exportOffline"><i class="material-icons">file_upload</i><strong>Çevrimdışı paylaşımı dışa aktar</strong><small>Proje verilerini yüklemeden taşınabilir inceleme dosyası oluşturun.</small></button><button type="button" @click="importOffline"><i class="material-icons">file_download</i><strong>Çevrimdışı paylaşımı içe aktar</strong><small>Daha önce dışa aktarılmış bir 3dBlock World paylaşım dosyasını açın.</small></button></div>
					<p class="dbw_collaboration_note"><i class="material-icons">security</i> Bulut paylaşımı isteğe bağlıdır. Çevrimdışı paylaşım dosyaları siz göndermeyi seçene kadar cihazınızda kalır.</p>
				</div>
			`,
		},
	});
	dialog.show();
}

type HistoryRecord = {id: string, label: string, created_at: string, size: number, data: string};
function historyKey(): string {
	return HISTORY_STORAGE_PREFIX + (Project?.uuid || 'no_project');
}
function loadHistory(): HistoryRecord[] {
	try {
		const value = JSON.parse(localStorage.getItem(historyKey()) || '[]');
		return Array.isArray(value) ? value : [];
	} catch (error) { return []; }
}
export function saveHistorySnapshot(label = 'Manuel anlık görüntü'): boolean {
	if (!Project || !Codecs?.project) return false;
	try {
		const data = Codecs.project.compile({raw: false, minify: true});
		const history = loadHistory();
		history.unshift({id: makeUuid(), label, created_at: new Date().toISOString(), size: data.length, data});
		localStorage.setItem(historyKey(), JSON.stringify(history.slice(0, MAX_HISTORY_ITEMS)));
		return true;
	} catch (error) {
		console.warn(`[${APP_NAME}] Could not save local history snapshot`, error);
		return false;
	}
}
function openHistory(): void {
	const dialog = new Dialog({
		id: 'dbw_history',
		title: text('dbw.history.title', '3dBlock World Sürüm Geçmişi'),
		width: 680,
		singleButton: true,
		component: {
			data() { return {history: loadHistory() as HistoryRecord[]}; },
			methods: {
				refresh() { this.history = loadHistory(); },
				create() { saveHistorySnapshot(); this.refresh(); },
				restore(snapshot: HistoryRecord) {
					if (!confirm(`“${snapshot.label}” anlık görüntüsü geri yüklensin mi? Kaydedilmemiş değişiklikleriniz en son anlık görüntüde kalacaktır.`)) return;
					try {
						Codecs.project.load(JSON.parse(snapshot.data), {path: 'backup.bbmodel', no_file: true});
						this.refresh();
					} catch (error) {
						Blockbench.showMessageBox({title: APP_NAME, message: `Anlık görüntü geri yüklenemedi: ${error}`, icon: 'error'});
					}
				},
				remove(snapshot: HistoryRecord) {
					const history = loadHistory().filter(item => item.id !== snapshot.id);
					localStorage.setItem(historyKey(), JSON.stringify(history));
					this.refresh();
				},
			},
			template: `
				<div class="dbw_history">
					<div class="dbw_library_toolbar"><span>Yerel anlık görüntüler bu projeyle birlikte bu cihazda saklanır.</span><button type="button" @click="create">Anlık görüntü oluştur</button></div>
					<div class="dbw_history_row" v-for="snapshot in history" :key="snapshot.id"><div><strong>{{ snapshot.label }}</strong><small>{{ new Date(snapshot.created_at).toLocaleString() }} · {{ Math.round(snapshot.size / 1024) }} KB</small></div><button type="button" @click="restore(snapshot)">Geri Yükle</button><button type="button" class="dbw_danger" @click="remove(snapshot)">Sil</button></div>
					<div class="dbw_empty_state" v-if="!history.length">Henüz anlık görüntü yok.</div>
				</div>
			`,
		},
	});
	dialog.show();
}

function openPresentationStudio(): void {
	const dialog = new Dialog({
		id: 'dbw_presentation',
		title: text('dbw.presentation.title', '3dBlock World Sunum Stüdyosu'),
		width: 540,
		form: {
			output: {label: 'Çıktı', type: 'select', options: {image: 'Yüksek kaliteli PNG render', gif: 'Döner Tabla GIF'}, value: 'image'},
			preset: {label: 'Kompozisyon önayarı', type: 'select', options: {thumbnail: 'Kare küçük resim · 1024 × 1024', social: 'Sosyal medya gönderisi · 1080 × 1080', portrait: 'Dikey gönderi · 1080 × 1350', presentation: 'Sunum · 1920 × 1080', custom: 'Özel boyutlar'}, value: 'presentation'},
			width: {label: 'Genişlik', type: 'number', value: 1920, min: 128, max: 4096, step: 1},
			height: {label: 'Yükseklik', type: 'number', value: 1080, min: 128, max: 4096, step: 1},
			anti_aliasing: {label: 'Kenar Yumuşatma (Anti-aliasing)', type: 'select', options: {off: 'Kapalı', msaa: 'MSAA', ssaa: 'Süper Örnekleme (SSAA)'}, value: 'ssaa', condition: form => form.output === 'image'},
			seconds: {label: 'Döner tabla süresi (saniye)', type: 'number', value: 5, min: 1, max: 30, step: 0.5, condition: form => form.output === 'gif'},
			fps: {label: 'GIF FPS', type: 'number', value: 20, min: 5, max: 60, step: 1, condition: form => form.output === 'gif'},
			turnspeed: {label: 'Dönüş hızı', type: 'number', value: 60, min: -180, max: 180, step: 1, condition: form => form.output === 'gif'},
		},
		onConfirm(result) {
			if (!Project || !Preview.selected) return;
			const presetSizes: Record<string, [number, number]> = {thumbnail: [1024, 1024], social: [1080, 1080], portrait: [1080, 1350], presentation: [1920, 1080]};
			const size = presetSizes[String(result.preset)] || [Number(result.width) || 1920, Number(result.height) || 1080];
			const metadata = getMetadata();
			if (metadata) metadata.presentation = {width: size[0], height: size[1], preset: result.preset, output: result.output, updated_at: new Date().toISOString()};
			if (result.output === 'gif') {
				(Screencam as any).createGif({format: 'gif', length_mode: 'turntable', length: result.seconds, fps: result.fps, resolution: size, turnspeed: result.turnspeed});
			} else {
				(Screencam as any).advancedScreenshot(Preview.selected, {angle_preset: 'view', resolution: size, anti_aliasing: result.anti_aliasing, shading: true, show_gizmos: false});
			}
		},
	}).show();
}

function localAssistant(prompt: string): string {
	const report = auditProject();
	const text = prompt.toLowerCase();
	if (text.includes('audit') || text.includes('kontrol') || text.includes('hata')) {
		return `Üretim denetimi sonucu: ${report.score}/100. ${report.issues.length ? report.issues.map(issue => `• ${issue.message}`).join('\n') : 'Sorun bulunamadı.'}`;
	}
	if (text.includes('bedrock')) return 'Bedrock için öneri: Bedrock Varlığı profilini seçin, benzersiz bir geometry tanımlayıcısı kullanın, en az bir kemik grubu oluşturun ve Dışa Aktarma Stüdyosu üzerinden paket alın.';
	if (text.includes('optimiz') || text.includes('performans')) return `Optimizasyon özeti: ${report.stats.cubes} küp, yaklaşık ${report.stats.triangles.toLocaleString()} üçgen, ${report.stats.textures} doku. Önce doku atlası ve gereksiz küpleri azaltmayı deneyin.`;
	if (text.includes('animasyon')) return 'Animasyon önerisi: duruş, yürüme, saldırı ve hasar kliplerini ayrı tutun; ortak kemik adlarını koruyun ve dışa aktarım öncesi zaman çizelgesi süresini kontrol edin.';
	return `Ben ${APP_NAME} yerel asistanıyım. “denetim”, “Bedrock”, “optimizasyon” veya “animasyon” yazarak proje raporu alabilirsiniz.`;
}
function openAIAssistant(): void {
	const dialog = new Dialog({
		id: 'dbw_ai_assistant',
		title: text('dbw.ai.title', '3dBlock World AI Asistanı'),
		width: 680,
		singleButton: true,
		component: {
			data() { return {prompt: '', response: 'Bu 3D modelle ilgili nasıl yardımcı olabilirim?', busy: false}; },
			methods: {
				async ask() {
					if (!this.prompt.trim()) return;
					this.busy = true;
					await new Promise(resolve => setTimeout(resolve, 120));
					this.response = localAssistant(this.prompt);
					this.busy = false;
				},
				use(value: string) { this.prompt = value; this.ask(); },
			},
			template: `
				<div class="dbw_ai_panel">
					<p class="dbw_ai_notice"><i class="material-icons">security</i> Yerel, salt okunur asistan. Proje verilerinizi hiçbir yere göndermez.</p>
					<div class="dbw_ai_suggestions"><button type="button" @click="use('Üretim denetimini çalıştır')">Denetim yap</button><button type="button" @click="use('Bu modeli nasıl optimize edebilirim?')">Modeli optimize et</button><button type="button" @click="use('Bunu Bedrock için hazırla')">Bedrock için hazırla</button></div>
					<textarea v-model="prompt" placeholder="Mevcut proje hakkında bir soru sorun…"></textarea><button type="button" @click="ask" :disabled="busy">{{ busy ? 'Düşünülüyor…' : 'Asistana sor' }}</button>
					<div class="dbw_ai_response"><strong>Asistan</strong><pre>{{ response }}</pre></div>
				</div>
			`,
		},
	});
	dialog.show();
}

type NamePlanItem = {element: any, kind: string, original: string, next: string};

function getStructureElements(): {groups: any[], cubes: any[], meshes: any[], elements: any[]} {
	const groups = typeof Group !== 'undefined' ? getArray(Group.all) : [];
	const cubes = typeof Cube !== 'undefined' ? getArray(Cube.all) : [];
	const meshes = typeof Mesh !== 'undefined' ? getArray(Mesh.all) : [];
	return {groups, cubes, meshes, elements: [...groups, ...cubes, ...meshes]};
}

function getElementKind(element: any, structure = getStructureElements()): string {
	if (structure.groups.includes(element)) return 'Group';
	if (structure.meshes.includes(element)) return 'Mesh';
	return 'Cube';
}

function safeElementName(value: string, fallback: string): string {
	let name = String(value || '').trim()
		.replace(/[^a-zA-Z0-9_.-]+/g, '_')
		.replace(/^[_\-.]+|[_\-.]+$/g, '')
		.slice(0, 48);
	if (!name || !/^[a-zA-Z0-9]/.test(name)) name = fallback;
	return name;
}

function getNamePlan(replaceFrom = '', replaceTo = ''): NamePlanItem[] {
	const structure = getStructureElements();
	const used = new Set<string>();
	return structure.elements.map((element, index) => {
		const kind = getElementKind(element, structure);
		const original = String(element.name || '').trim();
		const replaced = replaceFrom ? original.split(replaceFrom).join(replaceTo) : original;
		const base = safeElementName(replaced, `${kind.toLowerCase()}_${index + 1}`);
		let next = base;
		let suffix = 2;
		while (used.has(next)) next = `${base}_${suffix++}`;
		used.add(next);
		return {element, kind, original, next};
	});
}

function applyNamePlan(replaceFrom = '', replaceTo = ''): number {
	if (!Project) return 0;
	const structure = getStructureElements();
	const plan = getNamePlan(replaceFrom, replaceTo);
	const changed = plan.filter(item => item.original !== item.next);
	if (!changed.length) return 0;
	Undo.initEdit({outliner: true, elements: [...structure.cubes, ...structure.meshes], groups: structure.groups});
	changed.forEach(item => item.element.name = item.next);
	Undo.finishEdit('Normalize production names');
	touchProject();
	return changed.length;
}

function isDegenerateCube(cube: any): boolean {
	const from = cube?.from;
	const to = cube?.to;
	if (!from || !to) return false;
	return [0, 1, 2].some(axis => Number(to[axis]) <= Number(from[axis]));
}

function getSceneOptimizerReport() {
	const structure = getStructureElements();
	const namePlan = getNamePlan();
	return {
		nameChanges: namePlan.filter(item => item.original !== item.next).length,
		duplicateNames: getProjectStats().duplicateNames.length,
		degenerateCubes: structure.cubes.filter(isDegenerateCube).length,
		emptyGroups: structure.groups.filter(group => !getArray(group.children).length).length,
	};
}

function applySceneCleanup(options: {normalize: boolean, removeDegenerate: boolean, removeEmpty: boolean}): number {
	if (!Project) return 0;
	const structure = getStructureElements();
	const plan = options.normalize ? getNamePlan() : [];
	const changed = plan.filter(item => item.original !== item.next);
	const degenerate = options.removeDegenerate ? structure.cubes.filter(isDegenerateCube) : [];
	const emptyGroups = options.removeEmpty ? structure.groups.filter(group => !getArray(group.children).length) : [];
	if (!changed.length && !degenerate.length && !emptyGroups.length) return 0;

	Undo.initEdit({outliner: true, elements: [...structure.cubes, ...structure.meshes], groups: structure.groups});
	changed.forEach(item => item.element.name = item.next);
	degenerate.forEach(cube => cube.remove());
	emptyGroups.forEach(group => {
		if (!getArray(group.children).length) group.remove();
	});
	Undo.finishEdit('Optimize production scene');
	touchProject();
	return changed.length + degenerate.length + emptyGroups.length;
}

type TextureAuditItem = {texture: any, name: string, width: number, height: number, usages: number, external: boolean};

function getTextureAudit(): TextureAuditItem[] {
	const textures = typeof Texture !== 'undefined' ? getArray(Texture.all) : [];
	const structure = getStructureElements();
	return textures.map(texture => {
		let usages = 0;
		for (const element of [...structure.cubes, ...structure.meshes]) {
			for (const face of Object.values(element.faces || {}) as any[]) {
				if (face.texture === texture.uuid || face.getTexture?.() === texture) usages++;
			}
		}
		return {
			texture,
			name: String(texture.name || texture.uuid || 'Unnamed texture'),
			width: Number(texture.width || texture.uv_width || 0),
			height: Number(texture.height || texture.uv_height || 0),
			usages,
			external: !!texture.path && !texture.source,
		};
	});
}

type AnimationClipAudit = {animation: any, name: string, length: number, keyframes: number, loop: string};

function getAnimationAudit(): {clips: AnimationClipAudit[], keyframes: number, maxLength: number, looping: number, unnamed: number} {
	const animations = Project ? getArray(Project.animations) : [];
	const clips: AnimationClipAudit[] = animations.map((animation: any, index) => {
		let keyframes = 0;
		for (const animator of Object.values(animation.animators || {}) as any[]) {
			keyframes += getArray(animator.keyframes).length;
		}
		return {
			animation,
			name: String(animation.name || `Animation ${index + 1}`),
			length: Number(animation.length || 0),
			keyframes,
			loop: String(animation.loop || 'once'),
		};
	});
	return {
		clips,
		keyframes: clips.reduce((total, clip) => total + clip.keyframes, 0),
		maxLength: clips.reduce((max, clip) => Math.max(max, clip.length), 0),
		looping: clips.filter(clip => clip.loop === 'loop').length,
		unnamed: clips.filter(clip => !clip.animation.name).length,
	};
}

function openProjectMetadata(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const metadata = getMetadata() || {};
	new Dialog({
		id: 'dbw_project_metadata',
		title: text('dbw.metadata.title', 'Proje Metaverileri'),
		width: 600,
		form: {
			name: {label: 'Proje adı', type: 'text', value: Project.name || ''},
			identifier: {label: 'Kaynak tanımlayıcısı', type: 'text', value: Project.geometry_name || metadata.identifier || ''},
			namespace: {label: 'Namespace', type: 'text', value: metadata.namespace || 'benim_paketim'},
			target_profile: {label: 'Doğrulama hedefi', type: 'select', options: Object.fromEntries(Object.entries(TargetProfiles).map(([id, profile]) => [id, profile.name])), value: metadata.target_profile || 'game_asset'},
			author: {label: 'Yazar / stüdyo', type: 'text', value: metadata.author || ''},
			version: {label: 'Varlık sürümü', type: 'text', value: metadata.version || '1.0.0'},
			tags: {label: 'Etiketler', type: 'text', value: getArray(metadata.tags).join(', ')},
			description: {label: 'Açıklama', type: 'textarea', height: 90, value: metadata.description || ''},
		},
		onConfirm(result) {
			Project.name = String(result.name || '').trim();
			Project.geometry_name = safeElementName(String(result.identifier || ''), 'model').toLowerCase();
			Project[META_KEY] = {
				...metadata,
				version: 1,
				asset_version: String(result.version || '1.0.0').trim() || '1.0.0',
				target_profile: result.target_profile || 'game_asset',
				identifier: Project.geometry_name,
				namespace: slugify(String(result.namespace || 'benim_paketim'), 'benim_paketim'),
				author: String(result.author || '').trim(),
				tags: String(result.tags || '').split(',').map(tag => tag.trim()).filter(Boolean).slice(0, 16),
				description: String(result.description || '').trim(),
				updated_at: new Date().toISOString(),
			};
			touchProject();
			setProjectTitle(Project.name);
		},
	}).show();
}

function openProjectDashboard(): void {
	const dialog = new Dialog({
		id: 'dbw_project_dashboard',
		title: text('dbw.dashboard.title', 'Üretim Komut Merkezi'),
		width: 820,
		singleButton: true,
		component: {
			data() {
				const report = auditProject();
				return {report, animationAudit: getAnimationAudit(), metadata: getMetadata(false), historyCount: Project ? loadHistory().length : 0};
			},
			computed: {
				projectName() { return Project ? (Project.name || Project.geometry_name || 'İsimsiz proje') : 'Açık proje yok'; },
				identifierReady() { return !!(this.metadata?.identifier || Project?.geometry_name); },
				budgetReady() { return !!Project && this.report.score >= 80; },
				textureReady() { return !!Project && (this.report.stats.textures > 0 || !!Format?.image_editor); },
				namingReady() { return !!Project && this.report.stats.duplicateNames.length === 0; },
			},
			methods: {
				refresh() {
					this.report = auditProject();
					this.animationAudit = getAnimationAudit();
					this.metadata = getMetadata(false);
					this.historyCount = Project ? loadHistory().length : 0;
				},
				openAudit() { dialog.hide(); openAuditDialog(); },
				openMetadata() { dialog.hide(); openProjectMetadata(); },
				openStructure() { dialog.hide(); openStructureTools(); },
				openModelTools() { dialog.hide(); openModelTools(); },
				openTextures() { dialog.hide(); openTextureQA(); },
				openTextureStudio() { dialog.hide(); openTextureStudio(); },
				openAtlas() { dialog.hide(); openAtlasBuilder(); },
				openAnimations() { dialog.hide(); openAnimationQA(); },
				openAnimationStudio() { dialog.hide(); openAnimationStudio(); },
				openGenerator() { dialog.hide(); openGeneratorStudio(); },
				openComponents() { dialog.hide(); openComponentStudio(); },
				openLOD() { dialog.hide(); openLODStudio(); },
				openBatch() { dialog.hide(); openBatchExport(); },
				openPack() { dialog.hide(); openPackWizard(); },
				openDiff() { dialog.hide(); openVersionDiff(); },
				openOptimizer() { dialog.hide(); openSceneOptimizer(); },
				openExport() { dialog.hide(); exportAssetPack().catch(error => Blockbench.showMessageBox({title: APP_NAME, message: String(error), icon: 'error'})); },
				openPresentation() { dialog.hide(); openPresentationStudio(); },
				openHistory() { dialog.hide(); openHistory(); },
				createProject() { dialog.hide(); Formats.free.new(); },
			},
			template: `
				<div class="dbw_dashboard">
					<div class="dbw_dashboard_header"><div><small class="dbw_kicker">ÜRETİM KOMUT MERKEZİ</small><h2>{{ projectName }}</h2><p>{{ report.profile.name }} <span v-if="report.stats">&middot; {{ report.stats.cubes }} küp &middot; {{ report.stats.textures }} doku</span></p></div><button type="button" @click="refresh"><i class="material-icons">refresh</i> Yenile</button></div>
					<div class="dbw_dashboard_stat_grid"><div class="dbw_dashboard_stat"><strong>{{ report.score }}</strong><span>Kalite puanı</span></div><div class="dbw_dashboard_stat"><strong>{{ report.stats.triangles.toLocaleString() }}</strong><span>Tahmini üçgen</span></div><div class="dbw_dashboard_stat"><strong>{{ animationAudit.clips.length }}</strong><span>Animasyon klibi</span></div><div class="dbw_dashboard_stat"><strong>{{ historyCount }}</strong><span>Yerel anlık görüntü</span></div></div>
					<div class="dbw_dashboard_grid">
						<section class="dbw_dashboard_card"><div class="dbw_card_heading"><div><small>TESLİMAT KONTROL LİSTESİ</small><h3>Teslimata hazır</h3></div><span class="dbw_score_badge" :class="{good: report.score >= 80, bad: report.score < 80}">{{ report.score }}/100</span></div><div class="dbw_checklist"><div class="dbw_checklist_row" :class="{done: budgetReady}"><i class="material-icons">{{ budgetReady ? 'check_circle' : 'radio_button_unchecked' }}</i><span>Hedef bütçe uygun</span></div><div class="dbw_checklist_row" :class="{done: identifierReady}"><i class="material-icons">{{ identifierReady ? 'check_circle' : 'radio_button_unchecked' }}</i><span>Kaynak tanımlayıcısı tanımlı</span></div><div class="dbw_checklist_row" :class="{done: namingReady}"><i class="material-icons">{{ namingReady ? 'check_circle' : 'radio_button_unchecked' }}</i><span>Yapı adları benzersiz</span></div><div class="dbw_checklist_row" :class="{done: textureReady}"><i class="material-icons">{{ textureReady ? 'check_circle' : 'radio_button_unchecked' }}</i><span>Doku hattı hazır</span></div></div><button type="button" class="dbw_button_primary" @click="openAudit">Tam üretim denetimini çalıştır</button></section>
						<section class="dbw_dashboard_card"><div class="dbw_card_heading"><div><small>PROJE KONTROLÜ</small><h3>Hızlıca geliştirin ve düzenleyin</h3></div><i class="material-icons dbw_card_icon">tune</i></div><div class="dbw_dashboard_button_grid"><button type="button" @click="openMetadata"><i class="material-icons">description</i><span><strong>Metaveri</strong><small>Kimlik, namespace ve sürüm</small></span></button><button type="button" @click="openGenerator"><i class="material-icons">auto_awesome</i><span><strong>Model Oluşturucu</strong><small>Karakter, ağaç, kılıç ve dekorlar</small></span></button><button type="button" @click="openModelTools"><i class="material-icons">straighten</i><span><strong>Model Araçları</strong><small>Ortala, zemine hizala, aynala</small></span></button><button type="button" @click="openStructure"><i class="material-icons">account_tree</i><span><strong>Yapı Araçları</strong><small>Adlar, kopyalar ve temizlik</small></span></button><button type="button" @click="openTextures"><i class="material-icons">texture</i><span><strong>Doku Denetimi</strong><small>Kullanım, boyutlar ve haritalar</small></span></button><button type="button" @click="openTextureStudio"><i class="material-icons">brush</i><span><strong>Doku Stüdyosu</strong><small>Çevir, renklendir ve piksel efektleri</small></span></button><button type="button" @click="openAnimations"><i class="material-icons">movie_filter</i><span><strong>Animasyon Denetimi</strong><small>Klipler, anahtar kareler ve döngü</small></span></button><button type="button" @click="openAnimationStudio"><i class="material-icons">movie_creation</i><span><strong>Animasyon Stüdyosu</strong><small>Duruş, yürüme ve saldırı başlangıçları</small></span></button></div></section>
						<section class="dbw_dashboard_card dbw_dashboard_card_wide"><div class="dbw_card_heading"><div><small>TESLİMAT ARAÇLARI</small><h3>Paketleyin, sunun ve kurtarın</h3></div></div><div class="dbw_dashboard_action_row"><button type="button" :disabled="!Project" @click="openOptimizer"><i class="material-icons">cleaning_services</i> Sahneyi optimize et</button><button type="button" :disabled="!Project" @click="openBatch"><i class="material-icons">inventory_2</i> Toplu dışa aktar</button><button type="button" :disabled="!Project" @click="openPack"><i class="material-icons">auto_awesome_motion</i> Paket sihirbazı</button><button type="button" :disabled="!Project" @click="openAtlas"><i class="material-icons">grid_on</i> Doku atlası oluştur</button><button type="button" :disabled="!Project" @click="openExport"><i class="material-icons">archive</i> Varlık paketini dışa aktar</button><button type="button" :disabled="!Project" @click="openPresentation"><i class="material-icons">camera_alt</i> Render stüdyosu</button><button type="button" :disabled="!Project" @click="openHistory"><i class="material-icons">history</i> Sürüm geçmişi</button><button type="button" :disabled="!Project" @click="openDiff"><i class="material-icons">compare</i> Sürümleri karşılaştır</button></div></section>
					</div>
					<div class="dbw_dashboard_footer"><span><i class="material-icons">shield</i> Yerel öncelikli iş akışı. Proje verileriniz paylaşmadığınız sürece bu cihazda kalır.</span><button type="button" @click="createProject" v-if="!Project">Proje oluştur</button></div>
				</div>
			`,
		},
	});
	dialog.show();
}

function openStructureTools(): void {
	const dialog = new Dialog({
		id: 'dbw_structure_tools',
		title: text('dbw.structure.title', 'Yapı ve Adlandırma Araçları'),
		width: 720,
		singleButton: true,
		component: {
			data() { return {replaceFrom: '', replaceTo: '', plan: getNamePlan()}; },
			computed: {
				preview() { return getNamePlan(this.replaceFrom, this.replaceTo).slice(0, 80); },
				changedCount() { return this.preview.filter(item => item.original !== item.next).length; },
			},
			methods: {
				refresh() { this.plan = getNamePlan(this.replaceFrom, this.replaceTo); },
				apply() {
					const count = applyNamePlan(this.replaceFrom, this.replaceTo);
					Blockbench.showQuickMessage(count ? `${count} ad standartlaştırıldı` : 'Adlar zaten temiz');
					this.refresh();
				},
			},
			template: `
				<div class="dbw_tool_panel dbw_structure_panel"><div class="dbw_tool_intro"><div><small>YAPICI OUTLINER TEMİZLİĞİ</small><h3>Dışa aktarım ve animasyon için öngörülebilir adlar</h3><p>Uygulamadan önce değişiklikleri önizleyin. Yinelenen, boş ve geçersiz adlar tek bir geri alınabilir işlemde düzeltilir.</p></div><span class="dbw_count_badge">{{ changedCount }} değişiklik</span></div><div class="dbw_replace_bar"><label>Bul <input v-model="replaceFrom" placeholder="isteğe bağlı metin"></label><label>Yeni değerle değiştir <input v-model="replaceTo" placeholder="yeni metin"></label><button type="button" @click="refresh"><i class="material-icons">preview</i> Önizle</button></div><div class="dbw_table"><div class="dbw_table_row dbw_table_header"><span>Tür</span><span>Mevcut ad</span><span>Üretim adı</span></div><div class="dbw_table_row" v-for="item in preview" :key="item.element.uuid || item.original + item.kind"><span class="dbw_type_label">{{ item.kind }}</span><span :class="{dbw_muted: !item.original}">{{ item.original || 'İsimsiz' }}</span><span :class="{dbw_changed: item.original !== item.next}">{{ item.next }}</span></div><div class="dbw_empty_state" v-if="!preview.length">Model elemanı bulunamadı.</div></div><p class="dbw_table_note" v-if="plan.length > preview.length">Toplam {{ plan.length }} elemandan ilk {{ preview.length }} tanesi gösteriliyor.</p><div class="dbw_tool_actions"><button type="button" class="dbw_button_primary" @click="apply" :disabled="!changedCount">Adları standartlaştır ve kopyaları düzelt</button></div></div>
			`,
		},
	});
	dialog.show();
}

function openSceneOptimizer(): void {
	const dialog = new Dialog({
		id: 'dbw_scene_optimizer',
		title: text('dbw.optimizer.title', 'Sahne Optimize Edici'),
		width: 620,
		singleButton: true,
		component: {
			data() { return {report: getSceneOptimizerReport(), normalize: true, removeDegenerate: false, removeEmpty: false}; },
			methods: {
				refresh() { this.report = getSceneOptimizerReport(); },
				apply() {
					if ((this.removeDegenerate || this.removeEmpty) && !confirm('Boş grupları veya sıfır hacimli küpleri silmek istiyor musunuz? Bu işlem geri alınabilir.')) return;
					const count = applySceneCleanup({normalize: this.normalize, removeDegenerate: this.removeDegenerate, removeEmpty: this.removeEmpty});
					Blockbench.showQuickMessage(count ? `${count} sahne temizlik işlemi uygulandı` : 'Sahne zaten optimize edilmiş');
					this.refresh();
				},
			},
			template: `
				<div class="dbw_tool_panel"><div class="dbw_tool_intro"><div><small>GÜVENLİ, GERİ ALINABİLİR TEMİZLİK</small><h3>Dışa aktarma sürprizlerini önleyin</h3><p>Taramayı inceleyin, düzeltmeleri seçin ve tek bir işlemde uygulayın.</p></div><button type="button" @click="refresh"><i class="material-icons">refresh</i></button></div><div class="dbw_optimizer_grid"><div><strong>{{ report.nameChanges }}</strong><span>Standartlaştırılacak adlar</span></div><div><strong>{{ report.duplicateNames }}</strong><span>Yinelenen ad grupları</span></div><div><strong>{{ report.degenerateCubes }}</strong><span>Sıfır hacimli küpler</span></div><div><strong>{{ report.emptyGroups }}</strong><span>Boş gruplar</span></div></div><div class="dbw_option_list"><label><input type="checkbox" v-model="normalize"><span><strong>Adları standartlaştır ve kopyaları düzelt</strong><small>Geçersiz karakterleri dönüştürür ve adları benzersiz tutar.</small></span></label><label><input type="checkbox" v-model="removeDegenerate"><span><strong>Sıfır hacimli küpleri kaldır</strong><small>Görünür hacmi olmayan küpleri siler.</small></span></label><label><input type="checkbox" v-model="removeEmpty"><span><strong>Boş grupları kaldır</strong><small>Yalnızca zaten boş olan grupları siler.</small></span></label></div><div class="dbw_tool_actions"><button type="button" class="dbw_button_primary" @click="apply">Seçilen temizliği uygula</button></div></div>
			`,
		},
	});
	dialog.show();
}

function openTextureQA(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const dialog = new Dialog({
		id: 'dbw_texture_qa',
		title: text('dbw.texture_qa.title', 'Doku Kalite Denetçisi'),
		width: 760,
		singleButton: true,
		component: {
			data() { return {search: '', onlyUnused: false, items: getTextureAudit() as TextureAuditItem[]}; },
			computed: {
				filtered() {
					const term = this.search.toLowerCase().trim();
					return this.items.filter(item => (!term || `${item.name} ${item.width} ${item.height}`.toLowerCase().includes(term)) && (!this.onlyUnused || item.usages === 0));
				},
			},
			methods: {
				refresh() { this.items = getTextureAudit(); },
				select(item: TextureAuditItem) { item.texture.select?.(); },
				exportReport() {
					const report = this.items.map(item => ({name: item.name, width: item.width, height: item.height, usages: item.usages, external: item.external}));
					Filesystem.exportFile({resource_id: 'model', type: 'Doku Denetim Raporu', extensions: ['json'], name: `${slugify(Project.name || 'asset')}_dokular`, content: JSON.stringify({project: Project.name, generated_at: new Date().toISOString(), textures: report}, null, 2), savetype: 'text'});
				},
			},
			template: `
				<div class="dbw_tool_panel"><div class="dbw_library_toolbar dbw_qa_toolbar"><input v-model="search" placeholder="Dokularda ara..."><label class="dbw_inline_check"><input type="checkbox" v-model="onlyUnused"> Yalnızca kullanılmayanlar</label><button type="button" @click="refresh"><i class="material-icons">refresh</i></button><button type="button" @click="exportReport"><i class="material-icons">download</i> Rapor</button></div><div class="dbw_table dbw_texture_table"><div class="dbw_table_row dbw_table_header"><span>Doku</span><span>Çözünürlük</span><span>Kullanım</span><span>Durum</span></div><div class="dbw_table_row" v-for="item in filtered" :key="item.texture.uuid"><span><button type="button" class="dbw_link_button" @click="select(item)">{{ item.name }}</button></span><span>{{ item.width || '?' }} x {{ item.height || '?' }}</span><span>{{ item.usages }} yüzey</span><span><em class="dbw_status" :class="{warning: item.usages === 0}">{{ item.usages === 0 ? 'KULLANILMIYOR' : item.external ? 'HARİCİ' : 'HAZIR' }}</em></span></div><div class="dbw_empty_state" v-if="!filtered.length">Bu filtreyle eşleşen doku yok.</div></div></div>
			`,
		},
	});
	dialog.show();
}

function openAnimationQA(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const dialog = new Dialog({
		id: 'dbw_animation_qa',
		title: text('dbw.animation_qa.title', 'Animasyon Kalite Denetçisi'),
		width: 760,
		singleButton: true,
		component: {
			data() { return {audit: getAnimationAudit()}; },
			methods: {
				refresh() { this.audit = getAnimationAudit(); },
				openAnimate() { dialog.hide(); Modes.options.animate.select(); },
				select(animation: any) { animation.select?.(); },
			},
			template: `
				<div class="dbw_tool_panel"><div class="dbw_tool_intro"><div><small>ANİMASYON DENETİMİ</small><h3>Dışa aktarmadan önce klipleri inceleyin</h3><p>Mevcut projedeki her animasyon için süreyi, anahtar kare kapsamını ve döngü modunu kontrol edin.</p></div><div class="dbw_qa_summary"><strong>{{ audit.keyframes }}</strong><span>anahtar kare</span></div></div><div class="dbw_metrics dbw_metrics_compact"><div><strong>{{ audit.clips.length }}</strong><span>Klip</span></div><div><strong>{{ audit.looping }}</strong><span>Döngüde</span></div><div><strong>{{ audit.maxLength.toFixed(2) }}</strong><span>Maks. saniye</span></div><div><strong>{{ audit.unnamed }}</strong><span>İsimsiz</span></div></div><div class="dbw_table dbw_animation_table"><div class="dbw_table_row dbw_table_header"><span>Animasyon</span><span>Uzunluk</span><span>Anahtar Kare</span><span>Döngü</span></div><div class="dbw_table_row" v-for="clip in audit.clips" :key="clip.animation.uuid || clip.name"><span><button type="button" class="dbw_link_button" @click="select(clip.animation)">{{ clip.name }}</button></span><span>{{ clip.length.toFixed(2) }} sn</span><span>{{ clip.keyframes }}</span><span><em class="dbw_status" :class="{warning: clip.loop === 'once'}">{{ clip.loop }}</em></span></div><div class="dbw_empty_state" v-if="!audit.clips.length">Bu projede animasyon klibi bulunmuyor.</div></div><div class="dbw_tool_actions"><button type="button" @click="openAnimate"><i class="material-icons">movie</i> Animasyon Modunu Aç</button><button type="button" @click="refresh"><i class="material-icons">refresh</i> Yenile</button></div></div>
			`,
		},
	});
	dialog.show();
}

type GeneratorOptions = {
	preset: string
	name: string
	width: number
	height: number
	depth: number
	segments: number
	use_selected_group: boolean
	create_texture: boolean
	texture_color: any
};

function colorString(value: any, fallback = '#78a8ff'): string {
	if (typeof value === 'string' && value.trim()) return value;
	if (value?.toHexString) return value.toHexString();
	if (value?.toString) {
		const result = value.toString();
		if (typeof result === 'string' && result.startsWith('#')) return result;
	}
	return fallback;
}

function addGeneratedCube(parent: any, name: string, from: number[], to: number[], rotation = [0, 0, 0], origin?: number[]): any {
	const cube = new Cube({
		name: safeElementName(name, 'part'),
		from: from as ArrayVector3,
		to: to as ArrayVector3,
		rotation: rotation as ArrayVector3,
		origin: (origin || from.slice()) as ArrayVector3,
		autouv: settings.autouv?.value ? 1 : 0,
	}).init();
	cube.addTo(parent);
	return cube;
}

function createGeneratedTexture(name: string, color: string, size = 64): any {
	if (typeof Texture === 'undefined' || !Project) return null;
	const canvas = document.createElement('canvas');
	canvas.width = canvas.height = Math.clamp(Math.round(size), 16, 1024);
	const ctx = canvas.getContext('2d');
	if (!ctx) return null;
	ctx.imageSmoothingEnabled = false;
	ctx.fillStyle = color;
	ctx.fillRect(0, 0, canvas.width, canvas.height);
	ctx.fillStyle = 'rgba(255,255,255,.12)';
	ctx.fillRect(0, 0, canvas.width, Math.max(1, Math.round(canvas.height * .12)));
	ctx.fillStyle = 'rgba(0,0,0,.12)';
	ctx.fillRect(0, Math.round(canvas.height * .88), canvas.width, Math.max(1, Math.round(canvas.height * .12)));
	const texture = new Texture({name: `${slugify(name, 'generated')}.png`, folder: 'generated'}).fromDataURL(canvas.toDataURL('image/png')).add(false, true);
	texture.load();
	return texture;
}

function assignGeneratedTexture(cubes: any[], texture: any): void {
	if (!texture) return;
	for (const cube of cubes) {
		for (const face of Object.values(cube.faces || {}) as any[]) {
			face.texture = texture.uuid;
		}
	}
}

function generateModelPreset(options: GeneratorOptions): {group: any, cubes: any[], texture: any} {
	if (!Project) throw new Error('Create or open a project first');
	const width = Math.max(1, Math.round(Number(options.width) || 8));
	const height = Math.max(1, Math.round(Number(options.height) || 8));
	const depth = Math.max(1, Math.round(Number(options.depth) || 8));
	const segments = Math.clamp(Math.round(Number(options.segments) || 6), 2, 32);
	const rootName = safeElementName(options.name || 'Generated Asset', 'generated_asset');
	const parent = options.use_selected_group && Group.first_selected ? Group.first_selected : null;
	const group = new Group({name: rootName}).init();
	group.addTo(parent || 'root');
	const cubes: any[] = [];
	const x0 = -width / 2;
	const z0 = -depth / 2;
	const add = (name: string, from: number[], to: number[], rotation = [0, 0, 0], origin?: number[]) => {
		const cube = addGeneratedCube(group, name, from, to, rotation, origin);
		cubes.push(cube);
		return cube;
	};

	switch (options.preset) {
		case 'pillar': {
			const section = height / 3;
			add('base', [x0 - 1, 0, z0 - 1], [x0 + width + 1, 1, z0 + depth + 1]);
			add('shaft', [x0, 1, z0], [x0 + width, height - 1, z0 + depth]);
			add('cap', [x0 - 1, height - 1, z0 - 1], [x0 + width + 1, height, z0 + depth + 1]);
			if (section > 2) add('capital_detail', [x0 + 1, height - section, z0 + 1], [x0 + width - 1, height - section + 1, z0 + depth - 1]);
			break;
		}
		case 'stairs': {
			const stepDepth = depth / segments;
			const stepHeight = height / segments;
			for (let index = 0; index < segments; index++) {
				add(`step_${index + 1}`, [x0, 0, z0 + index * stepDepth], [x0 + width, (index + 1) * stepHeight, z0 + depth]);
			}
			break;
		}
		case 'arch': {
			const legWidth = Math.max(1, Math.floor(width / 4));
			const beamHeight = Math.max(1, Math.floor(height / 4));
			add('left_leg', [x0, 0, z0], [x0 + legWidth, height - beamHeight, z0 + depth]);
			add('right_leg', [x0 + width - legWidth, 0, z0], [x0 + width, height - beamHeight, z0 + depth]);
			add('arch_beam', [x0, height - beamHeight, z0], [x0 + width, height, z0 + depth]);
			break;
		}
		case 'barrel': {
			const bandHeight = Math.max(1, height / segments);
			for (let index = 0; index < segments; index++) {
				const curve = Math.sin((index / Math.max(1, segments - 1)) * Math.PI);
				const inset = Math.max(0, Math.round((width * .22) * (1 - curve)));
				add(`barrel_ring_${index + 1}`, [x0 + inset, index * bandHeight, z0 + inset], [x0 + width - inset, (index + 1) * bandHeight, z0 + depth - inset]);
			}
			break;
		}
		case 'tree': {
			const trunkWidth = Math.max(1, Math.round(width / 4));
			const trunkDepth = Math.max(1, Math.round(depth / 4));
			const trunkHeight = Math.max(2, Math.round(height * .58));
			add('trunk', [-trunkWidth / 2, 0, -trunkDepth / 2], [trunkWidth / 2, trunkHeight, trunkDepth / 2]);
			const foliageLayers = Math.max(2, Math.min(5, Math.round(segments / 2)));
			for (let index = 0; index < foliageLayers; index++) {
				const layerWidth = Math.max(2, width - index * Math.floor(width / (foliageLayers + 1)));
				const layerDepth = Math.max(2, depth - index * Math.floor(depth / (foliageLayers + 1)));
				const y = trunkHeight - index * Math.max(1, Math.floor(height / 12));
				add(`foliage_${index + 1}`, [-layerWidth / 2, y, -layerDepth / 2], [layerWidth / 2, y + Math.max(2, Math.floor(height / foliageLayers)), layerDepth / 2]);
			}
			break;
		}
		case 'sword': {
			const bladeWidth = Math.max(1, Math.round(width / 4));
			const handleHeight = Math.max(2, Math.round(height * .22));
			const guardHeight = Math.max(1, Math.round(height * .06));
			add('handle', [-bladeWidth / 2, 0, -depth / 4], [bladeWidth / 2, handleHeight, depth / 4]);
			add('guard', [-width / 2, handleHeight, -depth / 3], [width / 2, handleHeight + guardHeight, depth / 3]);
			add('blade', [-bladeWidth / 2, handleHeight + guardHeight, -depth / 4], [bladeWidth / 2, height, depth / 4]);
			add('blade_tip', [-bladeWidth / 4, height, -depth / 6], [bladeWidth / 4, height + Math.max(1, Math.floor(height / 8)), depth / 6]);
			break;
		}
		case 'character': {
			const bodyWidth = Math.max(2, Math.round(width * .55));
			const bodyDepth = Math.max(2, Math.round(depth * .45));
			const headSize = Math.max(2, Math.round(width * .48));
			const legHeight = Math.max(2, Math.round(height * .28));
			const bodyHeight = Math.max(2, Math.round(height * .42));
			add('body', [-bodyWidth / 2, legHeight, -bodyDepth / 2], [bodyWidth / 2, legHeight + bodyHeight, bodyDepth / 2]);
			add('head', [-headSize / 2, legHeight + bodyHeight, -headSize / 2], [headSize / 2, legHeight + bodyHeight + headSize, headSize / 2]);
			add('leg_left', [-bodyWidth / 2, 0, -bodyDepth / 2], [-Math.max(1, bodyWidth / 5), legHeight, bodyDepth / 2]);
			add('leg_right', [Math.max(1, bodyWidth / 5), 0, -bodyDepth / 2], [bodyWidth / 2, legHeight, bodyDepth / 2]);
			add('arm_left', [-bodyWidth / 2 - Math.max(1, width / 6), legHeight + bodyHeight - 1, -bodyDepth / 2], [-bodyWidth / 2, legHeight + bodyHeight - Math.max(1, height / 4), bodyDepth / 2]);
			add('arm_right', [bodyWidth / 2, legHeight + bodyHeight - 1, -bodyDepth / 2], [bodyWidth / 2 + Math.max(1, width / 6), legHeight + bodyHeight - Math.max(1, height / 4), bodyDepth / 2]);
			break;
		}
		case 'cube':
		default:
			add('main', [x0, 0, z0], [x0 + width, height, z0 + depth]);
			break;
	}

	let texture = null;
	if (options.create_texture && typeof Texture !== 'undefined') {
		texture = createGeneratedTexture(rootName, colorString(options.texture_color), Math.max(16, Math.max(width, height, depth) * 4));
		assignGeneratedTexture(cubes, texture);
	}
	Canvas.updateView({elements: cubes, element_aspects: {transform: true, geometry: true, faces: true}});
	Canvas.updateAllFaces();
	UVEditor.loadData();
	return {group, cubes, texture};
}

function openGeneratorStudio(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	new Dialog({
		id: 'dbw_generator_studio',
		title: '3dBlock World Model Oluşturucu Stüdyo',
		width: 600,
		form: {
			preset: {label: 'Oluşturucu şablonu', type: 'select', options: {character: 'Karakter / mob', tree: 'Ağaç / bitki örtüsü', sword: 'Kılıç / aksesuar', stairs: 'Merdiven', arch: 'Kemer / kapı aralığı', pillar: 'Sütun', barrel: 'Varil', cube: 'Basit küp'}, value: 'character'},
			name: {label: 'Varlık adı', type: 'text', value: 'Uretilen_Varlik'},
			width: {label: 'Genişlik', type: 'number', value: 8, min: 1, max: 128, step: 1},
			height: {label: 'Yükseklik', type: 'number', value: 16, min: 1, max: 256, step: 1},
			depth: {label: 'Derinlik', type: 'number', value: 8, min: 1, max: 128, step: 1},
			segments: {label: 'Segment / detay', type: 'number', value: 6, min: 2, max: 32, step: 1},
			use_selected_group: {label: 'Seçili grubun içine ekle', type: 'checkbox', value: false},
			create_texture: {label: 'Başlangıç dokusu oluştur', type: 'checkbox', value: true},
			texture_color: {label: 'Başlangıç rengi', type: 'color', value: '#78a8ff'},
		},
		onConfirm(result) {
			try {
				Undo.initEdit({outliner: true, elements: [], groups: [], textures: []});
				const output = generateModelPreset(result as GeneratorOptions);
				Undo.finishEdit(`${result.preset} varlığı üret`, {outliner: true, elements: output.cubes, groups: [output.group], textures: output.texture ? [output.texture] : []});
				touchProject();
				Blockbench.showQuickMessage(`${output.cubes.length} parça oluşturuldu`);
				updateQuickHub();
			} catch (error) {
				Blockbench.showMessageBox({title: APP_NAME, message: String(error), icon: 'error'});
				return false;
			}
		},
	}).show();
}

function getModelBounds(): {min: number[], max: number[], size: number[]} | null {
	const cubes = typeof Cube !== 'undefined' ? getArray(Cube.all) : [];
	if (!cubes.length) return null;
	const min = [Infinity, Infinity, Infinity];
	const max = [-Infinity, -Infinity, -Infinity];
	for (const cube of cubes) {
		for (let axis = 0; axis < 3; axis++) {
			min[axis] = Math.min(min[axis], Number(cube.from?.[axis] || 0));
			max[axis] = Math.max(max[axis], Number(cube.to?.[axis] || 0));
		}
	}
	return {min, max, size: max.map((value, axis) => value - min[axis])};
}

function applyModelUtility(operation: string, targetHeight = 16): number {
	if (!Project) return 0;
	if (operation === 'pivot_center' || operation === 'pivot_ground') {
		if (typeof Group === 'undefined') return 0;
		const groups = getArray(Group.selected).length ? getArray(Group.selected) : getArray(Group.all).filter(group => group.parent === 'root');
		const bounds = getModelBounds();
		if (!groups.length || !bounds) return 0;
		const pivot = [
			(bounds.min[0] + bounds.max[0]) / 2,
			operation === 'pivot_ground' ? bounds.min[1] : (bounds.min[1] + bounds.max[1]) / 2,
			(bounds.min[2] + bounds.max[2]) / 2,
		] as ArrayVector3;
		Undo.initEdit({groups});
		groups.forEach(group => group.transferOrigin?.(pivot));
		(Canvas as any).updateAllBones?.(groups);
		(Canvas as any).updateAll?.();
		Undo.finishEdit(operation === 'pivot_ground' ? 'Pivotları zemine hizala' : 'Pivotları ortala');
		touchProject();
		return groups.length;
	}
	if (typeof Cube === 'undefined') return 0;
	const cubes = getArray(Cube.all);
	const bounds = getModelBounds();
	if (!cubes.length || !bounds) return 0;
	const centerX = (bounds.min[0] + bounds.max[0]) / 2;
	const centerZ = (bounds.min[2] + bounds.max[2]) / 2;
	const scale = operation === 'scale' && bounds.size[1] > 0 ? Math.max(.01, Number(targetHeight) / bounds.size[1]) : 1;
	Undo.initEdit({elements: cubes, outliner: true});
	for (const cube of cubes) {
		const from = cube.from.slice();
		const to = cube.to.slice();
		if (operation === 'center') {
			cube.from[0] -= centerX;
			cube.to[0] -= centerX;
			cube.origin[0] -= centerX;
			cube.from[2] -= centerZ;
			cube.to[2] -= centerZ;
			cube.origin[2] -= centerZ;
		} else if (operation === 'ground') {
			const offset = bounds.min[1];
			cube.from[1] -= offset;
			cube.to[1] -= offset;
			cube.origin[1] -= offset;
		} else if (operation === 'mirror') {
			cube.from[0] = centerX - (to[0] - centerX);
			cube.to[0] = centerX - (from[0] - centerX);
			cube.origin[0] = centerX - (cube.origin[0] - centerX);
			if (cube.rotation) cube.rotation[1] = -Number(cube.rotation[1] || 0);
		} else if (operation === 'scale') {
			for (let axis = 0; axis < 3; axis++) {
				const pivot = axis === 1 ? bounds.min[1] : axis === 0 ? centerX : centerZ;
				cube.from[axis] = pivot + (from[axis] - pivot) * scale;
				cube.to[axis] = pivot + (to[axis] - pivot) * scale;
				cube.origin[axis] = pivot + (Number(cube.origin[axis] || 0) - pivot) * scale;
			}
		}
	}
	Canvas.updateView({elements: cubes, element_aspects: {transform: true, geometry: true, faces: true}});
	Canvas.updateAllFaces();
	UVEditor.loadData();
	Undo.finishEdit(`Model ${operation}`);
	touchProject();
	return cubes.length;
}

function openModelTools(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	new Dialog({
		id: 'dbw_model_tools',
		title: '3dBlock World Model Araçları',
		width: 620,
		singleButton: true,
		component: {
			data() { return {bounds: getModelBounds(), targetHeight: 16, status: 'Tüm işlemler geri alınabilir ve mevcut projeyi korur.'}; },
			methods: {
				refresh() { this.bounds = getModelBounds(); },
				apply(operation: string) {
					const count = applyModelUtility(operation, this.targetHeight);
					this.status = count ? `${count} küp güncellendi · Geri alma kullanılabilir.` : 'Küp geometrisi bulunamadı.';
					this.refresh();
				},
			},
			template: `<div class="dbw_tool_panel dbw_model_tools"><div class="dbw_tool_intro"><div><small>MODEL DÖNÜŞTÜRME ARAÇ SETİ</small><h3>Her varlığı üretime hazır hale getirin</h3><p>Tüm küp modelini tek tıklamayla ortalayın, zemine hizalayın, aynalayın veya ölçeklendirin. Pivot işlemleri görünür geometriyi korur.</p></div><i class="material-icons dbw_card_icon">straighten</i></div><div class="dbw_metrics dbw_metrics_compact" v-if="bounds"><div><strong>{{ bounds.size[0].toFixed(1) }}</strong><span>Genişlik</span></div><div><strong>{{ bounds.size[1].toFixed(1) }}</strong><span>Yükseklik</span></div><div><strong>{{ bounds.size[2].toFixed(1) }}</strong><span>Derinlik</span></div><div><strong>{{ bounds.size.join(' × ') }}</strong><span>Boyutlar</span></div></div><div class="dbw_model_tool_grid"><button type="button" @click="apply('center')"><i class="material-icons">filter_center_focus</i><span><strong>X / Z Ortala</strong><small>Modeli dünya merkezine yerleştirin.</small></span></button><button type="button" @click="apply('ground')"><i class="material-icons">vertical_align_bottom</i><span><strong>Zemine Hizala</strong><small>En alt noktayı Y = 0 seviyesine taşıyın.</small></span></button><button type="button" @click="apply('mirror')"><i class="material-icons">flip</i><span><strong>X Boyunca Aynala</strong><small>Tüm küpleri model merkezi boyunca yansıtın.</small></span></button><button type="button" @click="apply('pivot_center')"><i class="material-icons">my_location</i><span><strong>Pivotları Ortala</strong><small>Seçili veya kök kemik pivotlarını hizalayın.</small></span></button><button type="button" @click="apply('pivot_ground')"><i class="material-icons">vertical_align_bottom</i><span><strong>Pivotları Zemine Hizala</strong><small>Pivotları model tabanına yerleştirin.</small></span></button><label><span>Hedef Yükseklik</span><input type="number" min="1" max="1024" step="1" v-model.number="targetHeight"><button type="button" @click="apply('scale')"><i class="material-icons">height</i> Yüksekliğe Ölçekle</button></label></div><p class="dbw_export_status"><i class="material-icons">info</i>{{ status }}</p></div>`,
		},
	}).show();
}

type SelectionSetMap = Record<string, string[]>;

function getSelectionSets(): SelectionSetMap {
	const metadata = getMetadata();
	if (!metadata) return {};
	if (!metadata.selection_sets || typeof metadata.selection_sets !== 'object' || Array.isArray(metadata.selection_sets)) metadata.selection_sets = {};
	return metadata.selection_sets as SelectionSetMap;
}

function getCurrentProductionSelection(): any[] {
	const nodes = [...getArray(Outliner.selected), ...getArray(typeof Group !== 'undefined' ? Group.selected : [])];
	return [...new Map(nodes.filter(node => node?.uuid).map(node => [node.uuid, node])).values()];
}

function findProductionNode(uuid: string): any {
	return getStructureElements().elements.find(element => element.uuid === uuid);
}

function selectProductionNodes(nodes: any[]): number {
	const clean = [...new Map(nodes.filter(node => node?.uuid).map(node => [node.uuid, node])).values()];
	if (typeof unselectAllElements === 'function') unselectAllElements();
	clean.forEach((node, index) => {
		if (typeof node.select === 'function') node.select(index === 0 ? undefined : {ctrlOrCmd: true} as any);
	});
	return clean.length;
}

function createComponentGroup(name: string, duplicate = false): number {
	if (!Project) return 0;
	const selected = getArray(Outliner.selected).slice();
	if (!selected.length) return 0;
	Undo.initEdit({outliner: true, elements: selected, groups: []});
	const group = new Group({name: safeElementName(name, 'component'), origin: [0, 0, 0] as ArrayVector3, rotation: [0, 0, 0] as ArrayVector3}).init();
	const moved: any[] = [];
	selected.forEach(element => {
		const item = duplicate ? element.duplicate() : element;
		item.addTo(group);
		moved.push(item);
	});
	group.isOpen = true;
	group.select();
	Undo.finishEdit(duplicate ? 'Kopyalanan bileşeni oluştur' : 'Seçimi bileşen olarak grupla', {outliner: true, elements: moved, groups: [group], selection: true});
	touchProject();
	return moved.length;
}

function getLODSourceCubes(): any[] {
	const selected = getArray(typeof Cube !== 'undefined' ? Cube.selected : []);
	return selected.length ? selected : getArray(typeof Cube !== 'undefined' ? Cube.all : []);
}

function createLODProxy(level: number, keepPercent: number): number {
	if (!Project || typeof Cube === 'undefined' || typeof Group === 'undefined') return 0;
	const source = getLODSourceCubes();
	if (!source.length) return 0;
	const keep = Math.max(1, Math.min(source.length, Math.round(source.length * Math.clamp(Number(keepPercent) || 50, 1, 100) / 100)));
	const ranked = source.slice().sort((a, b) => {
		const volume = (cube: any) => Math.abs(Number(cube.to?.[0] - cube.from?.[0])) * Math.abs(Number(cube.to?.[1] - cube.from?.[1])) * Math.abs(Number(cube.to?.[2] - cube.from?.[2]));
		return volume(b) - volume(a);
	}).slice(0, keep);
	Undo.initEdit({outliner: true, elements: [], groups: []});
	const group = new Group({name: `LOD${Math.max(1, Math.round(level))}_proxy`, origin: [0, 0, 0] as ArrayVector3}).init();
	const copies: any[] = [];
	ranked.forEach(cube => {
		const copy = cube.duplicate();
		copy.name = safeElementName(`${cube.name}_lod${level}`, `cube_lod${level}`);
		copy.addTo(group);
		copies.push(copy);
	});
	group.export = false;
	group.visibility = false;
	group.isOpen = true;
	group.select();
	Undo.finishEdit(`LOD${level} proxy oluştur`, {outliner: true, elements: copies, groups: [group], selection: true});
	touchProject();
	return copies.length;
}

function removeLODProxies(): number {
	if (typeof Group === 'undefined') return 0;
	const proxies = getArray(Group.all).filter(group => /^LOD[0-9]+_proxy$/i.test(String(group.name || '')));
	if (!proxies.length) return 0;
	Undo.initEdit({outliner: true, groups: proxies});
	proxies.forEach(group => group.remove());
	Undo.finishEdit('LOD proxylerini kaldır');
	touchProject();
	return proxies.length;
}

function openComponentStudio(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	new Dialog({
		id: 'dbw_component_studio',
		title: '3dBlock World Bileşen Stüdyosu',
		width: 740,
		singleButton: true,
		component: {
			data() { return {sets: clone(getSelectionSets()), setName: 'ana_parcalar', search: '', duplicate: false, status: 'Yeniden kullanılabilir seçim grupları kaydedin veya seçili küpleri bileşene dönüştürün.'}; },
			computed: {
				matching() {
					const term = String(this.search || '').toLowerCase().trim();
					return getStructureElements().elements.filter(element => !term || String(element.name || '').toLowerCase().includes(term)).slice(0, 60);
				},
			},
			methods: {
				refresh() { this.sets = clone(getSelectionSets()); },
				saveSet() {
					const ids = getCurrentProductionSelection().map(node => node.uuid);
					if (!ids.length) { this.status = 'Önce en az bir eleman veya grup seçin.'; return; }
					const key = safeElementName(this.setName, 'secim_grubu');
					getSelectionSets()[key] = ids;
					touchProject();
					this.status = `${ids.length} düğüm ${key} olarak kaydedildi.`;
					this.refresh();
				},
				loadSet(name: string) {
					const ids = getSelectionSets()[name] || [];
					const count = selectProductionNodes(ids.map(findProductionNode).filter(Boolean));
					this.status = `${count} düğüm ${name} grubundan seçildi.`;
				},
				removeSet(name: string) {
					delete getSelectionSets()[name];
					touchProject();
					this.refresh();
				},
				selectMatches() {
					const count = selectProductionNodes(this.matching);
					this.status = `${count} eşleşen düğüm seçildi.`;
				},
				selectOne(item: any) {
					selectProductionNodes([item]);
					this.status = `${item.name || 'İsimsiz'} seçildi.`;
				},
				groupSelection() {
					const count = createComponentGroup(this.setName, this.duplicate);
					this.status = count ? `${count} eleman ${this.duplicate ? 'kopyalanarak' : 'gruplanarak'} ${safeElementName(this.setName, 'bilesen')} oluşturuldu.` : 'Önce bir veya daha fazla outliner elemanı seçin.';
				},
			},
			template: `<div class="dbw_tool_panel dbw_component_studio"><div class="dbw_tool_intro"><div><small>YENİDEN KULLANILABİLİR MONTAJ İŞ AKIŞI</small><h3>Tekrarlanan işleri bileşenlere dönüştürün</h3><p>Seçim setlerini proje içine kaydedin, anında geri yükleyin ve parçaları geri alma desteğiyle gruplayın veya çoğaltın.</p></div><i class="material-icons dbw_card_icon">account_tree</i></div><div class="dbw_component_columns"><section><div class="dbw_section_label">Seçim grupları</div><div class="dbw_replace_bar"><input v-model="setName" placeholder="Grup veya bileşen adı"><button type="button" @click="saveSet"><i class="material-icons">bookmark_add</i> Seçimi kaydet</button></div><div class="dbw_saved_set" v-for="(ids, name) in sets" :key="name"><span><strong>{{ name }}</strong><small>{{ ids.length }} düğüm</small></span><button type="button" @click="loadSet(name)"><i class="material-icons">check_box</i></button><button type="button" class="dbw_danger" @click="removeSet(name)"><i class="material-icons">delete</i></button></div><div class="dbw_empty_state" v-if="!Object.keys(sets).length">Henüz kayıtlı seçim grubu yok.</div></section><section><div class="dbw_section_label">Bileşen eylemleri</div><label class="dbw_inline_check"><input type="checkbox" v-model="duplicate"> Seçili geometriyi çoğalt</label><button type="button" class="dbw_button_primary dbw_full_button" @click="groupSelection"><i class="material-icons">create_new_folder</i> Bileşen grubu oluştur</button><div class="dbw_section_label">Ada göre seç</div><div class="dbw_replace_bar"><input v-model="search" placeholder="Eleman, grup veya mesh ara"><button type="button" @click="selectMatches"><i class="material-icons">select_all</i> Eşleşenleri seç</button></div><div class="dbw_match_list"><button type="button" v-for="item in matching" :key="item.uuid" @click="selectOne(item)"><span>{{ item.name || 'İsimsiz' }}</span><small>{{ item.type || 'eleman' }}</small></button></div></section></div><p class="dbw_export_status"><i class="material-icons">info</i>{{ status }}</p></div>`,
		},
	}).show();
}

function openLODStudio(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	new Dialog({
		id: 'dbw_lod_studio',
		title: '3dBlock World LOD ve Varyant Stüdyosu',
		width: 650,
		singleButton: true,
		component: {
			data() { return {level: 1, keepPercent: 50, sourceCount: getLODSourceCubes().length, status: 'En büyük küplerden gizli, dışa aktarılmayan bir proxy oluşturur. İnceledikten sonra dışa aktarmayı etkinleştirebilirsiniz.'}; },
			methods: {
				refresh() { this.sourceCount = getLODSourceCubes().length; },
				build() {
					const count = createLODProxy(this.level, this.keepPercent);
					this.status = count ? `LOD${this.level} proxy'si ${count} küp ile oluşturuldu. Varsayılan olarak gizlidir ve dışa aktarmadan hariç tutulur.` : 'Küp geometrisi bulunamadı. Önce küpleri seçin veya model oluşturun.';
					this.refresh();
				},
				remove() {
					if (!confirm('Tüm üretilmiş LOD proxy gruplarını silmek istiyor musunuz? Bu işlem geri alınabilir.')) return;
					const count = removeLODProxies();
					this.status = count ? `${count} LOD proxy grubu silindi.` : 'Üretilmiş LOD proxy grubu bulunamadı.';
					this.refresh();
				},
			},
			template: `<div class="dbw_tool_panel dbw_lod_studio"><div class="dbw_tool_intro"><div><small>PERFORMANS VARYANTLARI</small><h3>Hafif çalışma zamanı modelleri hazırlayın</h3><p>En önemli geometriden gerçek bir proxy grubu oluşturun. Üretilen LOD onaylayana kadar gizli kalır ve dışa aktarılmaz.</p></div><i class="material-icons dbw_card_icon">layers</i></div><div class="dbw_lod_summary"><div><strong>{{ sourceCount }}</strong><span>Kaynak küp</span></div><div><strong>%{{ keepPercent }}</strong><span>Koruma oranı</span></div><div><strong>LOD{{ level }}</strong><span>Varyant seviyesi</span></div></div><div class="dbw_studio_form"><label>Varyant seviyesi<select v-model.number="level"><option :value="1">LOD1 · orta</option><option :value="2">LOD2 · düşük</option><option :value="3">LOD3 · mobil</option></select></label><label>Koruma yüzdesi <input type="range" min="5" max="100" step="5" v-model.number="keepPercent"><output>%{{ keepPercent }}</output></label></div><div class="dbw_tool_actions"><button type="button" @click="refresh"><i class="material-icons">refresh</i> Kaynak sayısını yenile</button><button type="button" @click="remove"><i class="material-icons">delete_sweep</i> Üretilen LODları kaldır</button><button type="button" class="dbw_button_primary" @click="build"><i class="material-icons">layers</i> LOD proxy oluştur</button></div><p class="dbw_export_status"><i class="material-icons">info</i>{{ status }}</p></div>`,
		},
	}).show();
}

function autoMapSelectedCubes(mode: string): number {
	if (!Project || typeof Cube === 'undefined') return 0;
	const cubes = getArray(Cube.selected).length ? getArray(Cube.selected) : getArray(Cube.all);
	if (!cubes.length) return 0;
	Undo.initEdit({elements: cubes});
	cubes.forEach(cube => {
		cube.setUVMode(mode === 'box');
		cube.autouv = 1;
		cube.mapAutoUV();
	});
	Canvas.updateAllFaces();
	UVEditor.loadData();
	Undo.finishEdit('Auto map production UVs');
	touchProject();
	return cubes.length;
}

function parseHexColor(value: string): [number, number, number] {
	const hex = colorString(value).replace('#', '').padEnd(6, 'f').slice(0, 6);
	return [parseInt(hex.slice(0, 2), 16) || 255, parseInt(hex.slice(2, 4), 16) || 255, parseInt(hex.slice(4, 6), 16) || 255];
}

function applyTextureStudioEffect(texture: any, effect: string, tint: string, strength: number): void {
	if (!texture) throw new Error('Select a texture first');
	const target = texture.getActiveCanvas?.() || texture;
	const canvas = target.canvas;
	const ctx = target.ctx;
	if (!canvas || !ctx || !canvas.width || !canvas.height) throw new Error('Texture image is not ready');
	Undo.initEdit({textures: [texture], bitmap: true});
	texture.convertToInternal?.();
	const amount = Math.clamp(Number(strength) || 50, 0, 100) / 100;
	if (effect === 'flip_x' || effect === 'flip_y') {
		const copy = document.createElement('canvas');
		copy.width = canvas.width;
		copy.height = canvas.height;
		const copyCtx = copy.getContext('2d');
		if (!copyCtx) throw new Error('Unable to create texture buffer');
		copyCtx.imageSmoothingEnabled = false;
		copyCtx.translate(effect === 'flip_x' ? canvas.width : 0, effect === 'flip_y' ? canvas.height : 0);
		copyCtx.scale(effect === 'flip_x' ? -1 : 1, effect === 'flip_y' ? -1 : 1);
		copyCtx.drawImage(canvas, 0, 0);
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.drawImage(copy, 0, 0);
	} else {
		const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const rgb = parseHexColor(tint);
		for (let index = 0; index < data.data.length; index += 4) {
			const r = data.data[index], g = data.data[index + 1], b = data.data[index + 2];
			if (effect === 'grayscale') {
				const gray = Math.round(r * .299 + g * .587 + b * .114);
          data.data[index] = Math.round(r + (gray - r) * amount);
          data.data[index + 1] = Math.round(g + (gray - g) * amount);
          data.data[index + 2] = Math.round(b + (gray - b) * amount);
			} else if (effect === 'invert') {
				data.data[index] = Math.round(r + (255 - r - r) * amount);
				data.data[index + 1] = Math.round(g + (255 - g - g) * amount);
				data.data[index + 2] = Math.round(b + (255 - b - b) * amount);
			} else if (effect === 'tint') {
				data.data[index] = Math.round(r + (rgb[0] - r) * amount);
				data.data[index + 1] = Math.round(g + (rgb[1] - g) * amount);
				data.data[index + 2] = Math.round(b + (rgb[2] - b) * amount);
			} else if (effect === 'outline' && data.data[index + 3] === 0) {
				const x = Math.floor((index / 4) % canvas.width);
				const y = Math.floor(index / 4 / canvas.width);
				let edge = false;
				for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
					const nx = x + dx, ny = y + dy;
					if (nx >= 0 && ny >= 0 && nx < canvas.width && ny < canvas.height && data.data[(ny * canvas.width + nx) * 4 + 3] > 0) edge = true;
				}
				if (edge) {
					data.data[index] = rgb[0]; data.data[index + 1] = rgb[1]; data.data[index + 2] = rgb[2]; data.data[index + 3] = Math.round(255 * amount);
				}
			}
		}
		ctx.putImageData(data, 0, 0);
	}
	texture.updateChangesAfterEdit?.();
	texture.updateSource?.(texture.canvas.toDataURL('image/png', 1));
	Canvas.updateAllFaces();
	UVEditor.loadData();
	Undo.finishEdit(`Texture ${effect}`);
	touchProject();
}

function openTextureStudio(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const dialog = new Dialog({
		id: 'dbw_texture_studio',
		title: '3dBlock World Doku Stüdyosu',
		width: 680,
		singleButton: true,
		component: {
			data() { return {textures: getTextureAudit(), textureId: Texture.selected?.uuid || getTextureAudit()[0]?.texture.uuid || '', effect: 'grayscale', tint: '#ffffff', strength: 70, uvMode: 'box', status: 'Bir doku seçin ve işlem uygulayın.'}; },
			computed: {selected() { return this.textures.find(item => item.texture.uuid === this.textureId)?.texture; }},
			methods: {
				refresh() { this.textures = getTextureAudit(); },
				apply() {
					try {
						applyTextureStudioEffect(this.selected, this.effect, this.tint, this.strength);
						this.status = `${this.selected.name} dokusuna ${this.effect} efekti uygulandı`;
						this.refresh();
					} catch (error) { this.status = String(error); }
				},
				selectTexture() { const item = this.textures.find(item => item.texture.uuid === this.textureId); item?.texture.select?.(); },
				autoMap() {
					const count = autoMapSelectedCubes(this.uvMode);
					this.status = count ? `${count} küp ${this.uvMode} modunda otomatik UV eşlendi.` : 'Küp geometrisi bulunamadı.';
				},
			},
			template: `<div class="dbw_tool_panel dbw_texture_studio"><div class="dbw_tool_intro"><div><small>PİKSEL HASSASİYETİNDE DOKU HATTI</small><h3>Modelden ayrılmadan dokuları düzenleyin</h3><p>Çevirin, gri tonlamaya alın, tersine çevirin, renklendirin veya dış çizgi ekleyin. Her düzenleme geri alınabilir ve 3D önizlemeyi anında günceller.</p></div><i class="material-icons dbw_card_icon">brush</i></div><div class="dbw_studio_form"><label>Doku<select v-model="textureId" @change="selectTexture"><option v-for="item in textures" :value="item.texture.uuid">{{ item.name }} · {{ item.width || '?' }}×{{ item.height || '?' }}</option></select></label><label>Efekt<select v-model="effect"><option value="grayscale">Gri Tonlama</option><option value="invert">Renkleri Tersine Çevir</option><option value="tint">Renklendir</option><option value="outline">Alfa Dış Çizgisi</option><option value="flip_x">Yatay Çevir</option><option value="flip_y">Dikey Çevir</option></select></label><label>Yoğunluk <input type="range" min="0" max="100" v-model.number="strength"><output>%{{ strength }}</output></label><label v-if="effect === 'tint' || effect === 'outline'">Renk <input type="color" v-model="tint"></label><label>Otomatik UV Modu<select v-model="uvMode"><option value="box">Kutu UV (Box)</option><option value="per_face">Yüzey Başına UV</option></select></label></div><p class="dbw_export_status" v-if="status"><i class="material-icons">info</i>{{ status }}</p><div class="dbw_tool_actions"><button type="button" @click="refresh"><i class="material-icons">refresh</i> Yenile</button><button type="button" @click="autoMap"><i class="material-icons">grid_on</i> Seçili/tüm küpleri otomatik eşle</button><button type="button" class="dbw_button_primary" @click="apply"><i class="material-icons">auto_fix_high</i> Efekti uygula</button></div></div>`,
		},
	});
	dialog.show();
}

function addAnimationKeyframe(animator: any, channel: string, time: number, values: number[]): void {
	if (!animator?.channels?.[channel]) return;
	animator.createKeyframe({data_points: [{x: String(values[0]), y: String(values[1]), z: String(values[2])}]}, time, channel, false, false);
}

function generateStarterAnimation(preset: string, name: string, length: number, loop: string): any {
	if (!Project || typeof Animation === 'undefined') throw new Error('Bu format için animasyon araçları kullanılamaz');
	const groups = getArray(Group.all).filter(group => group.constructor.animator);
	if (!groups.length) throw new Error('Animasyon oluşturmak için önce kemik (bone) grupları oluşturun');
	const duration = Math.clamp(Number(length) || 1, .2, 60);
	Undo.initEdit({animations: []});
	const animation = new (Blockbench as any).Animation({name: `animation.${slugify(Project.geometry_name || 'asset', 'asset')}.${slugify(name || preset, preset)}`, loop, length: duration}).add(false) as any;
	for (const group of groups) {
		const animator = animation.getBoneAnimator(group);
		if (!animator) continue;
		const label = String(group.name || '').toLowerCase();
		const isHead = /head|neck|eye|kafa|boyun|goz/.test(label);
		const isLimb = /arm|leg|foot|hand|wing|kol|bacak|ayak|el|kanat/.test(label);
		if (preset === 'idle') {
			addAnimationKeyframe(animator, 'rotation', 0, [0, 0, 0]);
			addAnimationKeyframe(animator, 'rotation', duration / 2, [0, isHead ? 2 : 0, isLimb ? 2 : 0]);
			addAnimationKeyframe(animator, 'rotation', duration, [0, 0, 0]);
		} else if (preset === 'walk') {
			const phase = /left|l_|_l|sol/.test(label) ? 1 : -1;
			const amount = isLimb ? 25 * phase : isHead ? 2 : 0;
			addAnimationKeyframe(animator, 'rotation', 0, [amount, 0, 0]);
			addAnimationKeyframe(animator, 'rotation', duration / 2, [-amount, 0, 0]);
			addAnimationKeyframe(animator, 'rotation', duration, [amount, 0, 0]);
		} else {
			addAnimationKeyframe(animator, 'rotation', 0, [0, 0, 0]);
			addAnimationKeyframe(animator, 'rotation', duration * .2, [isLimb ? 20 : 0, isHead ? -8 : 0, 0]);
			addAnimationKeyframe(animator, 'rotation', duration * .55, [isLimb ? -35 : 0, isHead ? 8 : 0, 0]);
			addAnimationKeyframe(animator, 'rotation', duration, [0, 0, 0]);
		}
	}
	animation.setLength(duration);
	animation.select();
	Undo.finishEdit(`${preset} animasyonu üret`, {animations: [animation]});
	touchProject();
	return animation;
}

function openAnimationStudio(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	new Dialog({
		id: 'dbw_animation_studio',
		title: '3dBlock World Animasyon Stüdyosu',
		width: 600,
		form: {
			preset: {label: 'Başlangıç animasyonu', type: 'select', options: {idle: 'Bekleme / Nefes alma', walk: 'Yürüme döngüsü', attack: 'Saldırı hareketi'}, value: 'idle'},
			name: {label: 'Animasyon adı', type: 'text', value: 'idle'},
			length: {label: 'Süre (saniye)', type: 'number', value: 1, min: .2, max: 60, step: .1},
			loop: {label: 'Döngü modu', type: 'select', options: {loop: 'Döngü (Loop)', once: 'Bir kez (Once)', hold: 'Tut (Hold)'}, value: 'loop'},
		},
		onConfirm(result) {
			try {
				const animation = generateStarterAnimation(result.preset, result.name, result.length, result.loop);
				Blockbench.showQuickMessage(`${animation.name} animasyonu oluşturuldu`);
				updateQuickHub();
			} catch (error) {
				Blockbench.showMessageBox({title: APP_NAME, message: String(error), icon: 'error'});
				return false;
			}
		},
		}).show();
}

type BatchExportOptions = {formats: string[], includeTextures: boolean, includeSource: boolean, runAudit: boolean, packTarget?: string};

const BatchExportChoices: Record<string, {label: string, extension: string}> = {
	bbmodel: {label: 'Düzenlenebilir 3dBlock World projesi', extension: 'bbmodel'},
	bedrock: {label: 'Minecraft Bedrock geometrisi', extension: 'json'},
	java_block: {label: 'Minecraft Java blok/eşya modeli', extension: 'json'},
	gltf: {label: 'Taşınabilir GLB modeli', extension: 'glb'},
	obj: {label: 'OBJ model + materyalleri', extension: 'obj'},
};

function addTextureEntries(archive: any, root = 'textures'): number {
	if (typeof Texture === 'undefined') return 0;
	let added = 0;
	for (const texture of getArray(Texture.all)) {
		const entry = dataUrlToZipEntry(texture.source);
		if (!entry) continue;
		const textureName = slugify(String(texture.name || texture.uuid || `texture_${added}`), `texture_${added}`)
			.replace(/\.[a-z0-9]+$/i, '') + '.png';
		archive.file(`${root}/${textureName}`, entry.data, {base64: entry.base64});
		added++;
	}
	return added;
}

async function compileBatchFormat(formatId: string): Promise<{files: {path: string, content: any, base64?: boolean}[], label: string}> {
	const choice = BatchExportChoices[formatId];
	if (!choice) throw new Error(`Bilinmeyen dışa aktarma formatı: ${formatId}`);
	if (formatId === 'bbmodel') {
		return {label: choice.label, files: [{path: `kaynak/proje.${choice.extension}`, content: Codecs.project.compile({raw: false, minify: false})}]};
	}
	if (formatId === 'bedrock') {
		const content = Codecs.bedrock.compile({});
		return {label: choice.label, files: [{path: `minecraft/bedrock/model.${choice.extension}`, content: typeof content === 'string' ? content : JSON.stringify(content, null, 2)}]};
	}
	if (formatId === 'java_block') {
		const content = Codecs.java_block.compile({});
		return {label: choice.label, files: [{path: `minecraft/java/model.${choice.extension}`, content: typeof content === 'string' ? content : JSON.stringify(content, null, 2)}]};
	}
	if (formatId === 'gltf') {
		const content = await Codecs.gltf.compile({encoding: 'binary', embed_textures: true, animations: true});
		return {label: choice.label, files: [{path: `portable/model.${choice.extension}`, content}]};
	}
	const content = Codecs.obj.compile({all_files: true});
	const files: {path: string, content: any, base64?: boolean}[] = [{path: 'portable/model.obj', content: content.obj}, {path: 'portable/materials.mtl', content: content.mtl}];
	for (const image of Object.values(content.images || {}) as any[]) {
		const entry = dataUrlToZipEntry(image?.source);
		if (entry) files.push({path: `portable/${slugify(String(image.name || 'texture'), 'texture').replace(/\.[a-z0-9]+$/i, '')}.png`, content: entry.data, base64: entry.base64});
	}
	return {label: choice.label, files};
}

async function exportBatchPackage(options: BatchExportOptions): Promise<void> {
	if (!Project) throw new Error('Önce bir proje oluşturun veya açın');
	if (!options.formats.length) throw new Error('En az bir çıktı formatı seçin');
	if (options.runAudit) {
		const report = auditProject();
		if (report.issues.some(issue => issue.severity === 'error')) throw new Error(`Dışa aktarma engellendi: üretim denetiminde ${report.issues.filter(issue => issue.severity === 'error').length} hata bulundu`);
	}
	const archive = new JSZip();
	const base = slugify(Project.name || Project.geometry_name || 'varlik', 'varlik');
	const metadata = getMetadata(false) || {};
	const manifest: any = {app: APP_NAME, project: Project.name || base, generated_at: new Date().toISOString(), formats: [], textures: 0, pack_target: options.packTarget || null};
	manifest.metadata = metadata;
	for (const formatId of options.formats) {
		const output = await compileBatchFormat(formatId);
		manifest.formats.push({id: formatId, label: output.label, files: output.files.map(file => file.path)});
		output.files.forEach(file => archive.file(file.path, file.content, file.base64 ? {base64: true} : undefined));
	}
	if (options.includeSource && !options.formats.includes('bbmodel')) {
		archive.file('kaynak/proje.bbmodel', Codecs.project.compile({raw: false, minify: false}));
		manifest.formats.push({id: 'bbmodel', label: BatchExportChoices.bbmodel.label, files: ['kaynak/proje.bbmodel']});
	}
	const identifier = `${slugify(metadata.namespace || 'benim_paketim', 'benim_paketim')}:${slugify(Project.geometry_name || metadata.identifier || base, base)}`;
	if (options.formats.includes('bedrock')) {
		const resourceUuid = makeUuid();
		const behaviorUuid = makeUuid();
		archive.file('minecraft/resource_pack/manifest.json', JSON.stringify({format_version: 2, header: {name: Project.name || base, description: '3dBlock World tarafından üretildi', uuid: resourceUuid, version: [1, 0, 0]}, modules: [{type: 'resources', uuid: makeUuid(), version: [1, 0, 0]}]}, null, 2));
		archive.file('minecraft/behavior_pack/manifest.json', JSON.stringify({format_version: 2, header: {name: `${Project.name || base} Davranış`, description: '3dBlock World tarafından üretildi', uuid: behaviorUuid, version: [1, 0, 0]}, modules: [{type: 'data', uuid: makeUuid(), version: [1, 0, 0]}], dependencies: [{uuid: resourceUuid, version: [1, 0, 0]}]}, null, 2));
		if (options.packTarget === 'bedrock_block') {
			const blockManifest = {format_version: '1.20.0', 'minecraft:block': {description: {identifier, menu_category: {category: 'construction'}}, components: {'minecraft:geometry': {identifier: `geometry.${slugify(Project.geometry_name || base, base)}`}}}};
			archive.file(`minecraft/behavior_pack/blocks/${base}.json`, JSON.stringify(blockManifest, null, 2));
		} else {
			const entityManifest = {format_version: '1.20.0', 'minecraft:entity': {description: {identifier, is_spawnable: true, is_summonable: true}, components: {'minecraft:health': {value: 20, max: 20}}}};
			archive.file(`minecraft/behavior_pack/entities/${base}.json`, JSON.stringify(entityManifest, null, 2));
		}
	}
	if (options.formats.includes('java_block')) {
		archive.file('minecraft/pack.mcmeta', JSON.stringify({pack: {pack_format: 15, description: Project.name || base}}, null, 2));
	}
	if (options.includeTextures) manifest.textures = addTextureEntries(archive);
	archive.file('export_manifest.json', JSON.stringify(manifest, null, 2));
	archive.file('README.md', [`# ${Project.name || base}`, '', `${APP_NAME} tarafından üretildi.`, '', 'Bu paket seçilen teslimat formatlarını ve dışa aktarma bildirimini içerir.'].join('\n'));
	const content = await archive.generateAsync({type: 'blob'});
	Filesystem.exportFile({resource_id: 'model', type: '3dBlock World Teslimat Paketi', extensions: ['zip'], name: `${base}_teslimat`, content, savetype: 'zip'});
}

function openBatchExport(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const defaultFormats: Record<string, boolean> = {bbmodel: true, bedrock: Format?.id === 'bedrock' || Format?.id === 'bedrock_block', java_block: Format?.id === 'java_block', gltf: true, obj: false};
	const dialog = new Dialog({
		id: 'dbw_batch_export',
		title: text('dbw.batch_export.title', 'Toplu Teslimat Paketi Oluşturucu'),
		width: 680,
		singleButton: true,
		component: {
			data() { return {formats: defaultFormats, choices: BatchExportChoices, includeTextures: true, includeSource: true, runAudit: true, busy: false, status: ''}; },
			methods: {
				async exportPackage() {
					this.busy = true;
					this.status = 'Teslimatlar hazırlanıyor...';
					try {
						await exportBatchPackage({formats: Object.keys(this.formats).filter(id => this.formats[id]), includeTextures: this.includeTextures, includeSource: this.includeSource, runAudit: this.runAudit});
						this.status = 'Paket hazırlandı.';
					} catch (error) {
						this.status = String(error instanceof Error ? error.message : error);
					} finally { this.busy = false; }
				},
			},
			template: `
			<div class="dbw_tool_panel dbw_batch_export"><div class="dbw_tool_intro"><div><small>TEK KAYNAK, ÇOKLU ÇIKTI</small><h3>Temiz bir teslimat paketi oluşturun</h3><p>Seçilen formatları kaynak proje, dokular ve manifest ile tek bir ZIP dosyasında toplayın.</p></div><i class="material-icons dbw_card_icon">inventory_2</i></div><div class="dbw_export_format_grid"><label v-for="(choice, id) in choices" :key="id"><input type="checkbox" v-model="formats[id]"><span><strong>{{ choice.label }}</strong><small>.{{ choice.extension }}</small></span></label></div><div class="dbw_option_list"><label><input type="checkbox" v-model="includeTextures"><span><strong>Dokuları dahil et</strong><small>Gömülü dokuları pakete kopyalar.</small></span></label><label><input type="checkbox" v-model="includeSource"><span><strong>Düzenlenebilir kaynağı her zaman dahil et</strong><small>Yukarıda seçilmese bile BBMODEL kaynağını ekler.</small></span></label><label><input type="checkbox" v-model="runAudit"><span><strong>Denetimde hata varsa dışa aktarımı engelle</strong><small>Uyarılar raporda görünür; hatalar paketi durdurur.</small></span></label></div><p class="dbw_export_status" v-if="status"><i class="material-icons">{{ busy ? 'sync' : 'info' }}</i>{{ status }}</p><div class="dbw_tool_actions"><button type="button" class="dbw_button_primary" @click="exportPackage" :disabled="busy"><i class="material-icons">archive</i>{{ busy ? 'Paket oluşturuluyor...' : 'Teslimat paketini oluştur' }}</button></div></div>
			`,
		},
	});
	dialog.show();
}

function openPackWizard(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const dialog = new Dialog({
		id: 'dbw_pack_wizard',
		title: text('dbw.pack_wizard.title', 'Minecraft Paket Sihirbazı'),
		width: 620,
		form: {
			target: {label: 'Teslimat hedefi', type: 'select', options: {bedrock_entity: 'Bedrock Varlık Paketi', bedrock_block: 'Bedrock Blok Paketi', java_item: 'Java Eşya / Blok Paketi', generic: 'Genel GLB Paketi'}, value: Format?.id === 'java_block' ? 'java_item' : Format?.id === 'bedrock_block' ? 'bedrock_block' : Format?.id === 'bedrock' ? 'bedrock_entity' : 'generic'},
			namespace: {label: 'Namespace', type: 'text', value: getMetadata(false)?.namespace || 'benim_paketim'},
			identifier: {label: 'Tanımlayıcı (Identifier)', type: 'text', value: Project.geometry_name || 'varligim'},
			version: {label: 'Paket sürümü', type: 'text', value: getMetadata(false)?.asset_version || '1.0.0'},
			include_textures: {label: 'Dokuları dahil et', type: 'checkbox', value: true},
		},
		onConfirm(result) {
			const target = String(result.target || 'generic');
			const metadata = getMetadata() || {};
			Project.geometry_name = slugify(String(result.identifier || 'varligim'), 'varligim');
			Project[META_KEY] = {...metadata, namespace: slugify(String(result.namespace || 'benim_paketim'), 'benim_paketim'), identifier: Project.geometry_name, asset_version: String(result.version || '1.0.0'), pack_target: target, target_profile: target === 'java_item' ? 'java_item' : target === 'generic' ? 'game_asset' : target, updated_at: new Date().toISOString()};
			touchProject();
			dialog.hide();
			const formats = target === 'java_item' ? ['java_block'] : target === 'generic' ? ['gltf'] : ['bedrock'];
			exportBatchPackage({formats, includeTextures: result.include_textures !== false, includeSource: true, runAudit: true, packTarget: target}).catch(error => Blockbench.showMessageBox({title: APP_NAME, message: String(error), icon: 'error'}));
		},
	});
	dialog.show();
}

type AtlasPlanItem = {name: string, width: number, height: number, x: number, y: number, page: number, texture: any};

function createAtlasPlan(maxSize = 2048, padding = 2): {items: AtlasPlanItem[], pages: number, maxSize: number} {
	const items: AtlasPlanItem[] = [];
	let x = padding;
	let y = padding;
	let rowHeight = 0;
	let page = 0;
	for (const item of getTextureAudit().filter(texture => texture.width > 0 && texture.height > 0).sort((a, b) => b.height * b.width - a.height * a.width)) {
		if (item.width + padding * 2 > maxSize || item.height + padding * 2 > maxSize) continue;
		if (x + item.width + padding > maxSize) { x = padding; y += rowHeight + padding; rowHeight = 0; }
		if (y + item.height + padding > maxSize) { page++; x = padding; y = padding; rowHeight = 0; }
		items.push({name: item.name, width: item.width, height: item.height, x, y, page, texture: item.texture});
		x += item.width + padding;
		rowHeight = Math.max(rowHeight, item.height);
	}
	return {items, pages: items.length ? page + 1 : 0, maxSize};
}

function loadImage(source: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error('Bir doku kaynağı yüklenemedi'));
		image.src = source;
	});
}

async function exportTextureAtlas(maxSize: number, padding: number): Promise<void> {
	if (!Project) throw new Error('Önce bir proje oluşturun veya açın');
	const plan = createAtlasPlan(maxSize, padding);
	if (!plan.items.length) throw new Error('Okunabilir boyutlara sahip bitmap dokusu bulunamadı');
	const archive = new JSZip();
	const mapping: any[] = [];
	for (let page = 0; page < plan.pages; page++) {
		const canvas = document.createElement('canvas');
		canvas.width = canvas.height = maxSize;
		const context = canvas.getContext('2d');
		if (!context) throw new Error('Canvas kullanılamıyor');
		context.imageSmoothingEnabled = false;
		for (const item of plan.items.filter(entry => entry.page === page)) {
			const source = item.texture.source;
			if (!source) continue;
			try {
				const image = await loadImage(source);
				context.drawImage(image, item.x, item.y, item.width, item.height);
				mapping.push({name: item.name, page, x: item.x, y: item.y, width: item.width, height: item.height, u0: item.x / maxSize, v0: item.y / maxSize, u1: (item.x + item.width) / maxSize, v1: (item.y + item.height) / maxSize});
			} catch (error) { console.warn(`[${APP_NAME}] Atlas skipped ${item.name}`, error); }
		}
		const data = canvas.toDataURL('image/png');
		const entry = dataUrlToZipEntry(data);
		if (entry) archive.file(`atlas_${page + 1}.png`, entry.data, {base64: entry.base64});
	}
	archive.file('atlas_mapping.json', JSON.stringify({project: Project.name, atlas_size: maxSize, padding, generated_at: new Date().toISOString(), textures: mapping}, null, 2));
	const content = await archive.generateAsync({type: 'blob'});
	Filesystem.exportFile({resource_id: 'texture', type: 'Doku Atlas Paketi', extensions: ['zip'], name: `${slugify(Project.name || 'varlik')}_atlas`, content, savetype: 'zip'});
}

function openAtlasBuilder(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const dialog = new Dialog({
		id: 'dbw_atlas_builder',
		title: text('dbw.atlas.title', 'Doku Atlası Oluşturucu'),
		width: 700,
		singleButton: true,
		component: {
			data() { return {size: 2048, padding: 2, plan: createAtlasPlan(2048, 2), busy: false, status: ''}; },
			methods: {
				refresh() { this.plan = createAtlasPlan(Number(this.size), Number(this.padding)); },
				async build() {
					this.busy = true;
					try { await exportTextureAtlas(Number(this.size), Number(this.padding)); this.status = 'Atlas paketi hazır.'; } catch (error) { this.status = String(error instanceof Error ? error.message : error); } finally { this.busy = false; }
				},
			},
			template: `<div class="dbw_tool_panel"><div class="dbw_tool_intro"><div><small>ATLAS + UV EŞLEME VERİSİ</small><h3>Çalışma zamanı teslimatı için dokuları paketleyin</h3><p>Atlas PNG sayfaları ve normalize edilmiş haritalama dosyası oluşturur. Kaynak proje değiştirilmez.</p></div><span class="dbw_count_badge">{{ plan.items.length }} eşlendi</span></div><div class="dbw_replace_bar"><label>Atlas boyutu <select v-model="size"><option :value="1024">1024</option><option :value="2048">2048</option><option :value="4096">4096</option></select></label><label>Boşluk (Padding) <input type="number" min="0" max="32" v-model="padding"></label><button type="button" @click="refresh"><i class="material-icons">preview</i> Önizle</button></div><div class="dbw_optimizer_grid"><div><strong>{{ plan.pages }}</strong><span>Atlas sayfası</span></div><div><strong>{{ plan.items.length }}</strong><span>Eşlenen doku</span></div><div><strong>{{ padding }}px</strong><span>Boşluk</span></div><div><strong>{{ size }}px</strong><span>Sayfa boyutu</span></div></div><div class="dbw_table dbw_atlas_table"><div class="dbw_table_row dbw_table_header"><span>Doku</span><span>Sayfa</span><span>Konum</span></div><div class="dbw_table_row" v-for="item in plan.items.slice(0, 80)" :key="item.name + item.page"><span>{{ item.name }}</span><span>{{ item.page + 1 }}</span><span>{{ item.x }}, {{ item.y }} &middot; {{ item.width }}x{{ item.height }}</span></div></div><p class="dbw_export_status" v-if="status"><i class="material-icons">info</i>{{ status }}</p><div class="dbw_tool_actions"><button type="button" class="dbw_button_primary" @click="build" :disabled="busy"><i class="material-icons">texture</i>{{ busy ? 'Atlas oluşturuluyor...' : 'Atlas paketini dışa aktar' }}</button></div></div>`,
		},
	});
	dialog.show();
}

function getSnapshotDiff(first: HistoryRecord, second: HistoryRecord) {
	const a = JSON.parse(first.data);
	const b = JSON.parse(second.data);
	function diffCollection(key: string) {
		const left = getArray(a[key]);
		const right = getArray(b[key]);
		const leftMap = new Map(left.map(item => [item.uuid || item.name, JSON.stringify(item)]));
		const rightMap = new Map(right.map(item => [item.uuid || item.name, JSON.stringify(item)]));
		let added = 0, removed = 0, changed = 0;
		for (const id of rightMap.keys()) { if (!leftMap.has(id)) added++; else if (leftMap.get(id) !== rightMap.get(id)) changed++; }
		for (const id of leftMap.keys()) if (!rightMap.has(id)) removed++;
		return {added, removed, changed};
	}
	return {elements: diffCollection('elements'), groups: diffCollection('groups'), animations: diffCollection('animations'), textures: diffCollection('textures')};
}

function openVersionDiff(): void {
	if (!Project) {
		Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
		return;
	}
	const dialog = new Dialog({
		id: 'dbw_version_diff',
		title: text('dbw.diff.title', 'Görsel Sürüm Karşılaştırması'),
		width: 720,
		singleButton: true,
		component: {
			data() { const history = loadHistory(); return {history, first: history[1]?.id || history[0]?.id || '', second: history[0]?.id || '', result: null as any}; },
			computed: {firstSnapshot() { return this.history.find(item => item.id === this.first); }, secondSnapshot() { return this.history.find(item => item.id === this.second); }},
			methods: {
				compare() {
					if (!this.firstSnapshot || !this.secondSnapshot || this.first === this.second) { this.result = null; return; }
					try { this.result = getSnapshotDiff(this.firstSnapshot, this.secondSnapshot); } catch (error) { this.result = null; }
				},
				refresh() { this.history = loadHistory(); this.compare(); },
			},
			template: `<div class="dbw_tool_panel"><div class="dbw_tool_intro"><div><small>ANLIK GÖRÜNTÜ KARŞILAŞTIRMASI</small><h3>Nelerin değiştiğini görün</h3><p>Mevcut projeye dokunmadan yerel sürümleri eleman, grup, animasyon ve doku bazında karşılaştırın.</p></div><button type="button" @click="refresh"><i class="material-icons">refresh</i></button></div><div class="dbw_diff_select"><label>Eski anlık görüntü<select v-model="first"><option v-for="snapshot in history" :value="snapshot.id">{{ snapshot.label }} - {{ new Date(snapshot.created_at).toLocaleString() }}</option></select></label><label>Yeni anlık görüntü<select v-model="second"><option v-for="snapshot in history" :value="snapshot.id">{{ snapshot.label }} - {{ new Date(snapshot.created_at).toLocaleString() }}</option></select></label><button type="button" class="dbw_button_primary" @click="compare">Karşılaştır</button></div><div class="dbw_diff_grid" v-if="result"><div v-for="(value, key) in result" :key="key"><strong>{{ value.added + value.removed + value.changed }}</strong><span>{{ key }}</span><small>+{{ value.added }} eklendi &middot; -{{ value.removed }} silindi &middot; {{ value.changed }} değişti</small></div></div><div class="dbw_empty_state" v-else>{{ history.length < 2 ? 'Sürümleri karşılaştırmak için en az iki anlık görüntü oluşturun.' : 'İki farklı anlık görüntü seçip Karşılaştır düğmesine basın.' }}</div></div>`,
		},
	});
	dialog.show();
}

// Keep the product layer visible in the actual workspace.
let autoDashboardProject = '';

function updateQuickHub(): void {
	const hub = document.getElementById('dbw_quick_hub');
	if (!hub) return;
	const projectOpen = !!Project;
	hub.classList.toggle('has_project', projectOpen);
	hub.querySelectorAll<HTMLButtonElement>('[data-requires-project]').forEach(button => {
		button.disabled = !projectOpen;
	});
	const status = hub.querySelector<HTMLElement>('[data-db-status]');
	if (!status) return;
	const icon = status.querySelector('i');
	const label = status.querySelector('span');
	if (projectOpen) {
		let score = 0;
		try {
			score = auditProject().score;
		} catch (error) {
			console.warn('Unable to refresh production status', error);
		}
		if (icon) icon.textContent = score >= 80 ? 'verified' : 'warning';
		if (label) label.textContent = `${Project.name || Project.geometry_name || 'İsimsiz proje'} · Kalite ${score}/100`;
	} else {
		if (icon) icon.textContent = 'info';
		if (label) label.textContent = 'Üretim araçlarını açmak için bir proje oluşturun veya açın';
	}
}

function ensureQuickHub(): void {
	if (document.getElementById('dbw_quick_hub')) {
		updateQuickHub();
		return;
	}
	const center = document.getElementById('center');
	const preview = document.getElementById('preview');
	if (!center) return;
	const hub = document.createElement('section');
	hub.id = 'dbw_quick_hub';
	hub.innerHTML = `
		<div class="dbw_quick_hub_brand"><i class="material-icons">dashboard_customize</i><span><strong>3dBlock World</strong><small>Üretim çalışma alanı</small></span></div>
		<div class="dbw_quick_hub_actions">
			<button type="button" data-db-action="new" title="Yeni bir proje oluştur"><i class="material-icons">add_box</i><span>Yeni proje</span></button>
			<button type="button" data-db-action="dashboard" data-requires-project title="Üretim komut merkezini aç"><i class="material-icons">dashboard</i><span>Komut merkezi</span></button>
			<button type="button" data-db-action="generator" data-requires-project title="Parametrik başlangıç modeli oluştur"><i class="material-icons">auto_awesome</i><span>Model Üretici</span></button>
			<button type="button" data-db-action="model_tools" data-requires-project title="Modeli ortala, zemine hizala, aynala veya ölçeklendir"><i class="material-icons">straighten</i><span>Model araçları</span></button>
			<button type="button" data-db-action="components" data-requires-project title="Seçim setlerini kaydet ve yeniden kullanılabilir bileşenler oluştur"><i class="material-icons">account_tree</i><span>Bileşenler</span></button>
			<button type="button" data-db-action="lod" data-requires-project title="Hafif LOD proxy oluştur"><i class="material-icons">layers</i><span>LOD</span></button>
			<button type="button" data-db-action="audit" data-requires-project title="Üretim denetimini çalıştır"><i class="material-icons">fact_check</i><span>Denetim</span></button>
			<button type="button" data-db-action="optimize" data-requires-project title="Sahneyi temizle ve optimize et"><i class="material-icons">cleaning_services</i><span>Optimize et</span></button>
			<button type="button" data-db-action="texture" data-requires-project title="Doku kullanımını ve kalitesini incele"><i class="material-icons">texture</i><span>Doku Denetimi</span></button>
			<button type="button" data-db-action="texture_studio" data-requires-project title="Piksel efektleri ve doku düzenleme uygula"><i class="material-icons">brush</i><span>Doku Stüdyosu</span></button>
			<button type="button" data-db-action="animation_studio" data-requires-project title="Başlangıç animasyonu üret"><i class="material-icons">movie_creation</i><span>Animasyon</span></button>
			<button type="button" data-db-action="batch" data-requires-project title="Çoklu format teslimat paketi oluştur"><i class="material-icons">inventory_2</i><span>Toplu dışa aktar</span></button>
			<button type="button" data-db-action="pack" data-requires-project title="Minecraft teslimat paketi oluştur"><i class="material-icons">auto_awesome_motion</i><span>Paket sihirbazı</span></button>
			<button type="button" data-db-action="render" data-requires-project title="Render veya döner tabla oluştur"><i class="material-icons">camera_alt</i><span>Render</span></button>
		</div>
		<div class="dbw_quick_hub_status" data-db-status><i class="material-icons">info</i><span>Üretim araçlarını açmak için bir proje oluşturun veya açın</span></div>
	`;
	const handlers: Record<string, () => void> = {
		new: () => Formats.free.new(),
		dashboard: openProjectDashboard,
		generator: openGeneratorStudio,
		model_tools: openModelTools,
		components: openComponentStudio,
		lod: openLODStudio,
		audit: openAuditDialog,
		optimize: openSceneOptimizer,
		texture: openTextureQA,
		texture_studio: openTextureStudio,
		animation_studio: openAnimationStudio,
		batch: openBatchExport,
		pack: openPackWizard,
		render: openPresentationStudio,
	};
	hub.querySelectorAll<HTMLButtonElement>('button[data-db-action]').forEach(button => {
		button.addEventListener('click', () => {
			const action = handlers[button.dataset.dbAction || ''];
			if (!action) return;
			if (button.hasAttribute('data-requires-project') && !Project) {
				Blockbench.showQuickMessage('Önce bir proje oluşturun veya açın');
				return;
			}
			action();
		});
	});
	center.insertBefore(hub, preview || null);
	updateQuickHub();
}

function scheduleVisibleDashboard(project?: any): void {
	if (!project || autoDashboardProject === project.uuid) return;
	autoDashboardProject = project.uuid;
	setTimeout(() => {
		if (Project !== project || document.querySelector('dialog#dbw_project_dashboard')) return;
		openProjectDashboard();
	}, 450);
}

function registerActions(): void {
	new Action('dbw_project_dashboard', {name: '3dBlock World Üretim Komut Merkezi', description: 'Tüm üretim sürecini tek bir kontrol panelinden yönetin', icon: 'dashboard', category: 'file', click: openProjectDashboard});
	new Action('dbw_project_studio', {name: '3dBlock World Proje Stüdyosu', description: 'Üretim hedefi ve metaverilerle yeni bir proje oluşturun', icon: 'add_box', category: 'file', click: openProjectStudio});
	new Action('dbw_project_metadata', {name: '3dBlock World Proje Metaverileri', description: 'Proje kimliğini, namespace, sürüm ve teslimat metaverilerini düzenleyin', icon: 'description', category: 'file', condition: () => !!Project, click: openProjectMetadata});
	new Action('dbw_structure_tools', {name: '3dBlock World Yapı ve Adlandırma Araçları', description: 'Adları standartlaştırın ve yinelenen yapı tanımlayıcılarını düzeltin', icon: 'account_tree', category: 'edit', condition: () => !!Project, click: openStructureTools});
	new Action('dbw_scene_optimizer', {name: '3dBlock World Sahne Optimize Edici', description: 'Boş grupları, sıfır hacimli küpleri ve geçersiz adları temizleyin', icon: 'cleaning_services', category: 'edit', condition: () => !!Project, click: openSceneOptimizer});
	new Action('dbw_generator_studio', {name: '3dBlock World Model Oluşturucu Stüdyo', description: 'Parametrik olarak üretilmiş karakterler, dekorlar ve yapılar oluşturun', icon: 'auto_awesome', category: 'edit', condition: () => !!Project, click: openGeneratorStudio});
	new Action('dbw_model_tools', {name: '3dBlock World Model Araçları', description: 'Model geometrisini ortalayın, zemine hizalayın, aynalayın ve ölçeklendirin', icon: 'straighten', category: 'edit', condition: () => !!Project, click: openModelTools});
	new Action('dbw_component_studio', {name: '3dBlock World Bileşen Stüdyosu', description: 'Seçim gruplarını kaydedin ve yeniden kullanılabilir bileşen montajları oluşturun', icon: 'account_tree', category: 'edit', condition: () => !!Project, click: openComponentStudio});
	new Action('dbw_lod_studio', {name: '3dBlock World LOD ve Varyant Stüdyosu', description: 'Performans bütçeleri için gizli hafif proxy varyantları oluşturun', icon: 'layers', category: 'edit', condition: () => !!Project, click: openLODStudio});
	new Action('dbw_texture_qa', {name: '3dBlock World Doku Kalite Denetçisi', description: 'Doku boyutlarını, kullanımını ve harici varlıkları inceleyin', icon: 'texture', category: 'file', condition: () => !!Project, click: openTextureQA});
	new Action('dbw_texture_studio', {name: '3dBlock World Doku Stüdyosu', description: 'Geri alma destekli piksel hassasiyetinde doku efektleri uygulayın', icon: 'brush', category: 'edit', condition: () => !!Project, click: openTextureStudio});
	new Action('dbw_atlas_builder', {name: '3dBlock World Doku Atlas Oluşturucu', description: 'Teslimat için atlas sayfaları ve UV eşleme verileri oluşturun', icon: 'grid_on', category: 'file', condition: () => !!Project, click: openAtlasBuilder});
	new Action('dbw_animation_qa', {name: '3dBlock World Animasyon Kalite Denetçisi', description: 'Animasyon kliplerini, anahtar kareleri, süreyi ve döngü modlarını inceleyin', icon: 'movie_filter', category: 'file', condition: () => !!Project, click: openAnimationQA});
	new Action('dbw_animation_studio', {name: '3dBlock World Animasyon Stüdyosu', description: 'Düzenlenebilir duruş, yürüme ve saldırı animasyonu başlangıçları oluşturun', icon: 'movie_creation', category: 'edit', condition: () => !!Project, click: openAnimationStudio});
	new Action('dbw_batch_export', {name: '3dBlock World Teslimat Paketi Oluşturucu', description: 'Kaynak, dokular ve bildirim ile birden çok hedef biçimini dışa aktarın', icon: 'inventory_2', category: 'file', condition: () => !!Project, click: openBatchExport});
	new Action('dbw_pack_wizard', {name: '3dBlock World Minecraft Teslimat Sihirbazı', description: 'Namespace, tanımlayıcı ve hedef paketi tek bir kılavuzlu akışta hazırlayın', icon: 'auto_awesome_motion', category: 'file', condition: () => !!Project, click: openPackWizard});
	new Action('dbw_export_preflight', {name: '3dBlock World Dışa Aktarma Ön Kontrolü', description: 'Güvenli olmayan dışa aktarmaları engelleyin ve teslimat uyarılarını inceleyin', icon: 'verified', category: 'file', condition: () => !!Project, click: openAuditDialog});
	new Action('dbw_version_diff', {name: '3dBlock World Görsel Sürüm Karşılaştırıcı', description: 'İki yerel anlık görüntüyü model, doku ve animasyon değişikliklerine göre karşılaştırın', icon: 'compare', category: 'file', condition: () => !!Project, click: openVersionDiff});
	new Action('dbw_workflow_guide', {name: '3dBlock World Üretim Rehberi', description: 'Profesyonel 3D varlık iş akışını öğrenin', icon: 'school', category: 'file', click: openWorkflowGuide});
	new Action('dbw_production_audit', {name: '3dBlock World Üretim Denetimi', description: 'Mevcut projeyi hedef bütçeye göre kontrol edin', icon: 'fact_check', category: 'file', condition: () => !!Project, click: openAuditDialog});
	new Action('dbw_export_studio', {name: '3dBlock World Dışa Aktarma Stüdyosu', description: 'Taşınabilir varlık paketi oluşturun', icon: 'archive', category: 'file', condition: () => !!Project, click: () => exportAssetPack().catch(error => Blockbench.showMessageBox({title: APP_NAME, message: String(error), icon: 'error'}))});
	new Action('dbw_asset_library', {name: '3dBlock World Varlık Kütüphanesi', description: 'Yeniden kullanılabilir yerel model ve doku varlıklarını yönetin', icon: 'collections', category: 'file', click: openAssetLibrary});
	new Action('dbw_presets', {name: '3dBlock World Üretim Presetleri', description: 'Hazır pozlar uygulayın ve animasyon ön ayarlarını açın', icon: 'accessibility_new', category: 'file', condition: () => !!Project, click: openProductionPresets});
	new Action('dbw_collaboration', {name: '3dBlock World İşbirliği', description: 'Canlı, çevrimiçi paylaşım ve çevrimdışı işbirliği araçlarını açın', icon: 'people', category: 'file', click: openCollaborationStudio});
	new Action('dbw_plugin_marketplace', {name: '3dBlock World Eklenti Mağazası', description: 'Üretim eklentilerini keşfedin ve yönetin', icon: 'extension', category: 'file', click: openPluginMarketplace});
	new Action('dbw_history', {name: '3dBlock World Sürüm Geçmişi', description: 'Yerel proje anlık görüntüleri oluşturun ve geri yükleyin', icon: 'history', category: 'file', condition: () => !!Project, click: openHistory});
	new Action('dbw_presentation', {name: '3dBlock World Sunum Stüdyosu', description: 'Yüksek kaliteli renderlar ve 360 döner tablalar (turntable) oluşturun', icon: 'movie', category: 'file', condition: () => !!Project, click: openPresentationStudio});
	new Action('dbw_render_studio', {name: '3dBlock World Render Stüdyosu', description: 'Model renderları ve döner tabla önizlemeleri dışa aktarın', icon: 'camera_alt', category: 'file', condition: () => !!Project, click: openPresentationStudio});
	new Action('dbw_ai_assistant', {name: '3dBlock World AI Asistanı', description: 'Mevcut proje için yerel üretim rehberliği ve ipuçları alın', icon: 'auto_awesome', category: 'file', click: openAIAssistant});
}

BARS.defineActions(registerActions);

// Persist product metadata inside .bbmodel files without modifying the
// upstream project serializer.
onVueSetup(() => {
	if (typeof Codecs !== 'undefined' && Codecs.project) {
		Codecs.project.on('compile', (event: any) => {
			if (event.model?.meta && Project) event.model.meta[META_KEY] = clone(getMetadata(false) || {});
		});
		Codecs.project.on('parse', (event: any) => {
			if (event.model?.meta?.[META_KEY] && Project) Project[META_KEY] = clone(event.model.meta[META_KEY]);
		});
	}
	// Debounced local versioning turns the existing edit event stream into a
	// useful recovery history without requiring a server.
	let timer: ReturnType<typeof setTimeout> | null = null;
	// @ts-ignore event names are extensible in the runtime API
	Blockbench.on('finish_edit', () => {
		if (timer) clearTimeout(timer);
		timer = setTimeout(() => saveHistorySnapshot('Automatic recovery snapshot'), 2500);
	});
	ensureQuickHub();
	// Show the command center after the requested project dialog is confirmed,
	// and keep the quick actions synchronized with tab/project changes.
	const handleProjectReady = ({project}) => {
		updateQuickHub();
		scheduleVisibleDashboard(project);
	};
	Blockbench.on('new_project', handleProjectReady);
	Blockbench.on('setup_project', handleProjectReady);
	Blockbench.on('select_project', ({project}) => {
		updateQuickHub();
		scheduleVisibleDashboard(project);
	});
	const handleProjectClosed = () => {
		autoDashboardProject = '';
		updateQuickHub();
	};
	Blockbench.on('select_no_project', handleProjectClosed);
	Blockbench.on('close_project', handleProjectClosed);
});

export const ThreeDBlockWorld = {
	appName: APP_NAME,
	profiles: TargetProfiles,
	auditProject,
	getProjectStats,
	getTextureAudit,
	getAnimationAudit,
	openProjectStudio,
	openProjectDashboard,
	openProjectMetadata,
	openStructureTools,
	openSceneOptimizer,
	openGeneratorStudio,
	openModelTools,
	openComponentStudio,
	openLODStudio,
	openTextureQA,
	openTextureStudio,
	openAtlasBuilder,
	openAnimationQA,
	openAnimationStudio,
	openBatchExport,
	openPackWizard,
	openExportPreflight: openAuditDialog,
	openVersionDiff,
	openWorkflowGuide,
	openAuditDialog,
	openAssetLibrary,
	openProductionPresets,
	openCollaborationStudio,
	openHistory,
	openPresentationStudio,
	openRenderStudio: openPresentationStudio,
	openAIAssistant,
	exportAssetPack,
	saveHistorySnapshot,
};

Object.assign(window, {ThreeDBlockWorld});
